import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {
  apiCreateOrUpdateFdLongTermCrops,
  apiCreateFdApplicationHistoryCommodities,
  apiDeleteFdApplicationLongTermCrop,
  apiDeleteFdProductionHistory,
  apiDeleteFdProductionPlan,
  apiGetAllFdCommodityTypes,
  apiGetAllFdCrops,
  apiGetAllFdLivestock,
  apiGetFdApplicationLongTermCropsByApplicationId,
  apiGetFdApplicationLivestockByApplicationId,
  apiGetFdProductionHistoryByApplicationId,
  apiGetFdProductionPlansByApplicationId,
  apiGetFdApplicationHistoryCommoditiesByApplicationId,
  apiGetProduce,
  apiUpdateFdProductionHistory,
  apiUpdateFdProductionPlans,
} from 'constants/Api';
import {
  IFdProductionHistory,
  IFdProductionPlan,
  IProduce,
  ISaveFdProductionPlanDto,
} from 'constants/interfaces';
import {EIrrigation} from 'constants/index';

import ApplicationJourneyStepLayout from './ApplicationJourneyStepLayout';
import ApplicationJourneySubStepProgress from './ApplicationJourneySubStepProgress';
import {useSupplierApplicationJourney} from './SupplierApplicationJourneyContext';
import {
  lshUseCommoditySelectionSubStep,
  lshUseLivestockSubStep,
  lshUseLongTermCropsSubStep,
  lshUseProductionPlanSubStep,
  lshUseShortTermHistorySubStep,
} from './steps/production-info/hooks';
import {
  CommoditySelectionSubStep,
  LivestockSubStep,
  LongTermCropsSubStep,
  ProductionPlanSubStep,
  ShortTermHistorySubStep,
} from './steps/production-info/substeps';
import {
  COMMODITY_GROUP_BY_NAME,
  EMPTY_LIVESTOCK_FORM,
  lshMapLivestockCatalogueItem,
  lshMapLivestockRecord,
} from './steps/production-info/productionInfo.utils';

type ProductionSubStepKey =
  | 'commodity-selection'
  | 'livestock'
  | 'long-term-crops'
  | 'production-plan'
  | 'short-term-production-history';

type CommodityGroupKey = 'livestock' | 'fruits' | 'vegetables' | 'grains';

type ICommodityType = {
  id: number;
  name: string;
};

type ILivestockCatalogueItem = {
  id: number;
  name: string;
  livestockType?: string | null;
  livestockProduct?: string | null;
};

type IApplicationLivestockRecord = {
  id?: number;
  quantity?: number | null;
  notes?: string | null;
  type?: string | null;
  productGrading?: string | null;
  sellOfPeriod?: string | null;
  price?: number | null;
  livestock?: ILivestockCatalogueItem | null;
  application?: {
    id: number;
  } | null;
};

type ILivestockFormState = {
  livestockId: string;
  quantity: string;
  type: string;
  productGrading: string;
  price: string;
  notes: string;
};

type SellOffFrequency = 'not-selling' | 'once-off' | 'custom';

type ILongTermCropRecord = {
  id?: number;
  blockNumber?: string | null;
  age?: number | null;
  numberOfTrees?: number | null;
  area?: number | null;
  trellies?: boolean | null;
  spacing?: string | null;
  irrigation?: string | null;
  type?: string | null;
  notes?: string | null;
  projectedYear?: number | null;
  projectedYield?: number | null;
  projectedPrice?: number | null;
  projectedArea?: number | null;
  historyYear1?: number | null;
  historyYield1?: number | null;
  historyArea1?: number | null;
  historyYear2?: number | null;
  historyYield2?: number | null;
  historyArea2?: number | null;
  historyYear3?: number | null;
  historyYield3?: number | null;
  historyArea3?: number | null;
  application?: {
    id: number;
  } | null;
  crop?: {
    id?: number | string | null;
    name?: string | null;
  } | null;
};

interface IEditableLongTermRow {
  rowId: string;
  id?: number;
  persistedBlockNumber?: string;
  cropId: string;
  blockNumber: string;
  age: string;
  numberOfTrees: string;
  area: string;
  trellies: '' | 'YES' | 'NO';
  spacing: string;
  irrigation: string;
  type: string;
  projectedYear: string;
  projectedYield: string;
  projectedPrice: string;
  projectedArea: string;
  historyYear1: string;
  historyYield1: string;
  historyArea1: string;
  historyYear2: string;
  historyYield2: string;
  historyArea2: string;
  historyYear3: string;
  historyYield3: string;
  historyArea3: string;
  notes: string;
}

type IEditableLongTermField = Exclude<
  keyof IEditableLongTermRow,
  'rowId' | 'id' | 'persistedBlockNumber'
>;

type ILongTermRowErrors = Partial<
  Record<IEditableLongTermField | 'general', string>
>;
type ILongTermTableErrors = Record<string, ILongTermRowErrors>;

interface ILongTermColumnDefinition {
  key: IEditableLongTermField;
  label: string;
  type: 'text' | 'number' | 'select' | 'textarea';
  placeholder?: string;
  options?: Array<{value: string; label: string}>;
  min?: string;
  step?: string;
  className?: string;
}

interface ILongTermTemplateColumnDefinition {
  key: IEditableLongTermField;
  header: string;
}

interface IEditableProductionPlanRow {
  rowId: string;
  id?: number;
  cropId: string;
  numberOfHectares: string;
  irrigation: string;
  areaUnderIrrigation: string;
  areaOfDryLand: string;
  estimatedYield: string;
  estimatedPrice: string;
  plantPopulation: string;
  plantingMonth: string;
  harvestMonth: string;
  harvestYear: string;
  notes: string;
}

type IEditableProductionPlanField =
  | 'numberOfHectares'
  | 'irrigation'
  | 'areaUnderIrrigation'
  | 'areaOfDryLand'
  | 'estimatedYield'
  | 'estimatedPrice'
  | 'plantPopulation'
  | 'plantingMonth'
  | 'harvestMonth'
  | 'harvestYear'
  | 'notes';

type IProductionPlanRowErrors = Partial<
  Record<IEditableProductionPlanField | 'general', string>
>;
type IProductionPlanTableErrors = Record<string, IProductionPlanRowErrors>;

interface IEditableHistoryRow {
  rowId: string;
  id?: number;
  cropId: string;
  year: number;
  numberOfHectaresFarmed: string;
  yieldPerHectare: string;
  plantMonth: string;
  plantYear: string;
  harvestMonth: string;
  harvestYear: string;
  notes: string;
}

type IEditableHistoryField =
  | 'numberOfHectaresFarmed'
  | 'yieldPerHectare'
  | 'plantMonth'
  | 'plantYear'
  | 'harvestMonth'
  | 'harvestYear'
  | 'notes';

type IHistoryRowErrors = Partial<
  Record<IEditableHistoryField | 'general', string>
>;
type IHistoryTableErrors = Record<string, IHistoryRowErrors>;

type DynamicProductionStep = {
  key: ProductionSubStepKey;
  title: string;
  description: string;
};

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const PRODUCTION_PLAN_MONTH_OPTIONS = MONTH_LABELS.map((label, index) => ({
  value: String(index + 1),
  label,
}));

const PRODUCTION_HISTORY_MONTH_OPTIONS = [
  {value: '1', label: 'January'},
  {value: '2', label: 'February'},
  {value: '3', label: 'March'},
  {value: '4', label: 'April'},
  {value: '5', label: 'May'},
  {value: '6', label: 'June'},
  {value: '7', label: 'July'},
  {value: '8', label: 'August'},
  {value: '9', label: 'September'},
  {value: '10', label: 'October'},
  {value: '11', label: 'November'},
  {value: '12', label: 'December'},
];

const PRODUCTION_HISTORY_YEAR_OPTIONS = (() => {
  const currentYear = new Date().getFullYear();
  const maxYear = currentYear + 6;
  const minYear = 1990;
  const options: Array<{value: string; label: string}> = [];

  for (let year = maxYear; year >= minYear; year -= 1) {
    options.push({value: String(year), label: String(year)});
  }

  return options;
})();

const PRODUCTION_HISTORY_DEFAULT_PLANT_MONTH = '11';
const PRODUCTION_HISTORY_DEFAULT_HARVEST_MONTH = '5';

const PRODUCTION_PLAN_IRRIGATION_OPTIONS = [
  {value: '', label: 'Select type'},
  {value: 'IRRIGATED', label: 'Irrigated'},
  {value: 'DRYLAND', label: 'Dryland'},
  {value: 'BOTH', label: 'Both'},
];

const LONG_TERM_IRRIGATION_OPTIONS = [
  {value: '', label: 'Select type'},
  {value: 'IRRIGATED', label: 'Irrigated'},
  {value: 'DRYLAND', label: 'Dryland'},
  {value: 'BOTH', label: 'Both'},
];

const LONG_TERM_TRELLIES_OPTIONS = [
  {value: '', label: 'Select'},
  {value: 'YES', label: 'Yes'},
  {value: 'NO', label: 'No'},
];

const LONG_TERM_COLUMNS: ILongTermColumnDefinition[] = [
  {
    key: 'cropId',
    label: 'Crop',
    type: 'select',
    placeholder: 'Select crop',
    className: 'is-col-crop',
  },
  {
    key: 'blockNumber',
    label: 'Block number',
    type: 'text',
    className: 'is-col-block',
  },
  {key: 'age', label: 'Age', type: 'number', min: '0', step: '1'},
  {
    key: 'numberOfTrees',
    label: 'Number of trees',
    type: 'number',
    min: '0',
    step: '1',
  },
  {key: 'area', label: 'Area (ha)', type: 'number', min: '0', step: '0.01'},
  {
    key: 'trellies',
    label: 'Trellies',
    type: 'select',
    options: LONG_TERM_TRELLIES_OPTIONS,
  },
  {key: 'spacing', label: 'Spacing (m)', type: 'number', min: '0', step: '1'},
  {
    key: 'irrigation',
    label: 'Irrigation',
    type: 'select',
    options: LONG_TERM_IRRIGATION_OPTIONS,
  },
  {
    key: 'type',
    label: 'Crop variety',
    type: 'select',
    placeholder: 'Select variety',
    className: 'is-col-wide',
  },
  {
    key: 'projectedYear',
    label: 'Projected year',
    type: 'number',
    min: '1900',
    step: '1',
  },
  {
    key: 'projectedYield',
    label: 'Projected yield',
    type: 'number',
    min: '0',
    step: '0.01',
  },
  {
    key: 'projectedPrice',
    label: 'Projected price',
    type: 'number',
    min: '0',
    step: '0.01',
  },
  {
    key: 'projectedArea',
    label: 'Projected area (ha)',
    type: 'number',
    min: '0',
    step: '0.01',
  },
  {
    key: 'historyYear1',
    label: 'History year 1',
    type: 'number',
    min: '1900',
    step: '1',
  },
  {
    key: 'historyYield1',
    label: 'History yield 1',
    type: 'number',
    min: '0',
    step: '0.01',
  },
  {
    key: 'historyArea1',
    label: 'History area 1 (ha)',
    type: 'number',
    min: '0',
    step: '0.01',
  },
  {
    key: 'historyYear2',
    label: 'History year 2',
    type: 'number',
    min: '1900',
    step: '1',
  },
  {
    key: 'historyYield2',
    label: 'History yield 2',
    type: 'number',
    min: '0',
    step: '0.01',
  },
  {
    key: 'historyArea2',
    label: 'History area 2 (ha)',
    type: 'number',
    min: '0',
    step: '0.01',
  },
  {
    key: 'historyYear3',
    label: 'History year 3',
    type: 'number',
    min: '1900',
    step: '1',
  },
  {
    key: 'historyYield3',
    label: 'History yield 3',
    type: 'number',
    min: '0',
    step: '0.01',
  },
  {
    key: 'historyArea3',
    label: 'History area 3 (ha)',
    type: 'number',
    min: '0',
    step: '0.01',
  },
  {key: 'notes', label: 'Notes', type: 'textarea', className: 'is-col-notes'},
];

const LONG_TERM_NUMERIC_FIELDS: IEditableLongTermField[] = [
  'age',
  'numberOfTrees',
  'area',
  'spacing',
  'projectedYear',
  'projectedYield',
  'projectedPrice',
  'projectedArea',
  'historyYear1',
  'historyYield1',
  'historyArea1',
  'historyYear2',
  'historyYield2',
  'historyArea2',
  'historyYear3',
  'historyYield3',
  'historyArea3',
];

const LONG_TERM_TEMPLATE_COLUMNS: ILongTermTemplateColumnDefinition[] = [
  {key: 'cropId', header: 'Crop'},
  {key: 'blockNumber', header: 'Block number'},
  {key: 'age', header: 'Age'},
  {key: 'numberOfTrees', header: 'Number of trees'},
  {key: 'area', header: 'Area (ha)'},
  {key: 'trellies', header: 'Trellies'},
  {key: 'spacing', header: 'Spacing (m)'},
  {key: 'irrigation', header: 'Irrigation'},
  {key: 'type', header: 'Crop variety'},
  {key: 'projectedYear', header: 'Projected year'},
  {key: 'projectedYield', header: 'Projected yield'},
  {key: 'projectedPrice', header: 'Projected price'},
  {key: 'projectedArea', header: 'Projected area (ha)'},
  {key: 'historyYear1', header: 'History year 1'},
  {key: 'historyYield1', header: 'History yield 1'},
  {key: 'historyArea1', header: 'History area 1 (ha)'},
  {key: 'historyYear2', header: 'History year 2'},
  {key: 'historyYield2', header: 'History yield 2'},
  {key: 'historyArea2', header: 'History area 2 (ha)'},
  {key: 'historyYear3', header: 'History year 3'},
  {key: 'historyYield3', header: 'History yield 3'},
  {key: 'historyArea3', header: 'History area 3 (ha)'},
  {key: 'notes', header: 'Notes'},
];

const LONG_TERM_TEMPLATE_HEADER_ALIASES: Record<
  string,
  IEditableLongTermField
> = {
  crop: 'cropId',
  'crop id': 'cropId',
  cropid: 'cropId',
  'block number': 'blockNumber',
  block: 'blockNumber',
  age: 'age',
  'number of trees': 'numberOfTrees',
  trees: 'numberOfTrees',
  area: 'area',
  'area ha': 'area',
  trellies: 'trellies',
  spacing: 'spacing',
  'spacing m': 'spacing',
  irrigation: 'irrigation',
  'crop variety': 'type',
  variety: 'type',
  type: 'type',
  'projected year': 'projectedYear',
  'projected yield': 'projectedYield',
  'projected price': 'projectedPrice',
  'projected area ha': 'projectedArea',
  'history year 1': 'historyYear1',
  'history yield 1': 'historyYield1',
  'history area 1 ha': 'historyArea1',
  'history year 2': 'historyYear2',
  'history yield 2': 'historyYield2',
  'history area 2 ha': 'historyArea2',
  'history year 3': 'historyYear3',
  'history yield 3': 'historyYield3',
  'history area 3 ha': 'historyArea3',
  notes: 'notes',
};

const LONG_TERM_TEMPLATE_SHEET_NAME = 'Long-term crops';
const LONG_TERM_LOOKUP_SHEET_NAME = 'Lookups';
const LONG_TERM_TEMPLATE_MIN_ROW_COUNT = 500;

function lshToNumberOrZero(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function lshCreateLongTermRowId(): string {
  return `lt-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

function lshToStringValue(value: unknown): string {
  return value === undefined || value === null ? '' : String(value);
}

function lshToIntOrZero(value: string): number {
  const trimmed = value.trim();
  if (!trimmed) return 0;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return 0;
  return Math.trunc(parsed);
}

function lshToIrrigationValue(value: string): EIrrigation | undefined {
  const normalized = value.trim().toUpperCase();
  if (!normalized) return undefined;
  if (normalized === EIrrigation.IRRIGATED) return EIrrigation.IRRIGATED;
  if (normalized === EIrrigation.DRYLAND) return EIrrigation.DRYLAND;
  if (normalized === EIrrigation.BOTH) return EIrrigation.BOTH;
  return undefined;
}

function lshToIsoPlantingDate(month: string): string {
  const monthNumber = Number(month);
  const safeMonth =
    Number.isFinite(monthNumber) && monthNumber >= 1 && monthNumber <= 12
      ? monthNumber
      : 1;
  const year = new Date().getFullYear();
  return new Date(Date.UTC(year, safeMonth - 1, 1)).toISOString();
}

function lshMonthFromIso(iso?: string | null): string {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  return String(parsed.getUTCMonth() + 1);
}

function lshMapProductionPlanRecordToEditableRow(
  record: IFdProductionPlan,
): IEditableProductionPlanRow | null {
  const cropIdRaw = record?.crop?.id;
  if (cropIdRaw === undefined || cropIdRaw === null) {
    return null;
  }

  const persistedId =
    record?.id !== undefined && record?.id !== null
      ? Number(record.id)
      : undefined;

  return {
    rowId: String(cropIdRaw),
    id: Number.isFinite(persistedId as number) ? persistedId : undefined,
    cropId: String(cropIdRaw),
    numberOfHectares:
      record?.numberOfHectares !== undefined &&
      record?.numberOfHectares !== null
        ? String(record.numberOfHectares)
        : '',
    irrigation: record?.irrigation
      ? String(record.irrigation).toUpperCase()
      : '',
    areaUnderIrrigation:
      record?.areaUnderIrrigation !== undefined &&
      record?.areaUnderIrrigation !== null
        ? String(record.areaUnderIrrigation)
        : '',
    areaOfDryLand:
      record?.areaOfDryLand !== undefined && record?.areaOfDryLand !== null
        ? String(record.areaOfDryLand)
        : '',
    estimatedYield:
      record?.estimatedYield !== undefined && record?.estimatedYield !== null
        ? String(record.estimatedYield)
        : '',
    estimatedPrice:
      record?.estimatedPrice !== undefined && record?.estimatedPrice !== null
        ? String(record.estimatedPrice)
        : '',
    plantPopulation:
      record?.plantPopulation !== undefined && record?.plantPopulation !== null
        ? String(record.plantPopulation)
        : '',
    plantingMonth: lshMonthFromIso(record?.plantingDate),
    harvestMonth:
      record?.harvestMonth !== undefined && record?.harvestMonth !== null
        ? String(record.harvestMonth)
        : '',
    harvestYear:
      record?.harvestYear !== undefined && record?.harvestYear !== null
        ? String(record.harvestYear)
        : '',
    notes: String(record?.notes || ''),
  };
}

function lshCreateEmptyProductionPlanRow(
  cropId: string,
): IEditableProductionPlanRow {
  return {
    rowId: cropId,
    cropId,
    numberOfHectares: '',
    irrigation: '',
    areaUnderIrrigation: '',
    areaOfDryLand: '',
    estimatedYield: '',
    estimatedPrice: '',
    plantPopulation: '',
    plantingMonth: '',
    harvestMonth: '',
    harvestYear: '',
    notes: '',
  };
}

function lshToOptionalNumberValue(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const stringValue = String(value).trim();
  if (!stringValue || stringValue === '0') return null;
  const parsed = Number(stringValue);
  return Number.isFinite(parsed) ? parsed : null;
}

function lshGetHistorySeasonDateDefaults(year: number): {
  plantMonth: string;
  plantYear: string;
  harvestMonth: string;
  harvestYear: string;
} {
  const seasonYear = Number.isFinite(year) ? year : new Date().getFullYear();
  return {
    plantMonth: PRODUCTION_HISTORY_DEFAULT_PLANT_MONTH,
    plantYear: String(seasonYear),
    harvestMonth: PRODUCTION_HISTORY_DEFAULT_HARVEST_MONTH,
    harvestYear: String(seasonYear + 1),
  };
}

function lshCreateEmptyHistoryRow(
  cropId: string,
  year: number,
): IEditableHistoryRow {
  const defaults = lshGetHistorySeasonDateDefaults(year);

  return {
    rowId: `${cropId}-${year}`,
    cropId,
    year,
    numberOfHectaresFarmed: '',
    yieldPerHectare: '',
    plantMonth: defaults.plantMonth,
    plantYear: defaults.plantYear,
    harvestMonth: defaults.harvestMonth,
    harvestYear: defaults.harvestYear,
    notes: '',
  };
}

function lshMapProductionHistoryRecordToEditableRow(
  record: IFdProductionHistory,
  cropId: string,
  year: number,
): IEditableHistoryRow {
  const defaults = lshGetHistorySeasonDateDefaults(year);
  const persistedId =
    record?.id !== undefined && record?.id !== null
      ? Number(record.id)
      : undefined;

  return {
    rowId: `${cropId}-${year}`,
    id: Number.isFinite(persistedId as number) ? persistedId : undefined,
    cropId,
    year,
    numberOfHectaresFarmed:
      record?.numberOfHectaresFarmed !== undefined &&
      record?.numberOfHectaresFarmed !== null
        ? String(record.numberOfHectaresFarmed)
        : '',
    yieldPerHectare:
      record?.yieldPerHectare !== undefined && record?.yieldPerHectare !== null
        ? String(record.yieldPerHectare)
        : '',
    plantMonth:
      record?.plantMonth !== undefined && record?.plantMonth !== null
        ? String(record.plantMonth)
        : defaults.plantMonth,
    plantYear:
      record?.plantYear !== undefined && record?.plantYear !== null
        ? String(record.plantYear)
        : defaults.plantYear,
    harvestMonth:
      record?.harvestMonth !== undefined && record?.harvestMonth !== null
        ? String(record.harvestMonth)
        : defaults.harvestMonth,
    harvestYear:
      record?.harvestYear !== undefined && record?.harvestYear !== null
        ? String(record.harvestYear)
        : defaults.harvestYear,
    notes: String(record?.notes || ''),
  };
}

function lshNormaliseHeader(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function lshNormaliseTrelliesValue(value: string): '' | 'YES' | 'NO' {
  const normalised = value.trim().toLowerCase();
  if (!normalised) return '';
  if (['yes', 'y', 'true', '1'].includes(normalised)) return 'YES';
  if (['no', 'n', 'false', '0'].includes(normalised)) return 'NO';
  return '';
}

function lshNormaliseIrrigationValue(value: string): string {
  const normalised = value.trim().toUpperCase();
  if (!normalised) return '';
  if (normalised.includes('IRRIG')) return 'IRRIGATED';
  if (normalised.includes('DRY')) return 'DRYLAND';
  if (normalised.includes('BOTH')) return 'BOTH';
  return normalised;
}

function lshGetOptionLabel(
  options: Array<{value: string; label: string}>,
  value: string,
): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

function lshGetOptionListValues(
  options: Array<{value: string; label: string}>,
  mode: 'value' | 'label' = 'label',
): string[] {
  return options
    .filter((option) => option.value !== '')
    .map((option) => (mode === 'value' ? option.value : option.label))
    .filter((option, index, arr) => arr.indexOf(option) === index);
}

function lshGetExcelColumnName(columnNumber: number): string {
  let value = columnNumber;
  let result = '';

  while (value > 0) {
    const remainder = (value - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    value = Math.floor((value - 1) / 26);
  }

  return result;
}

function lshGetNextDuplicateBlockNumber(
  originalBlockNumber: string,
  existingRows: IEditableLongTermRow[],
): string {
  const base = originalBlockNumber.trim() || 'Block';
  const existing = new Set(
    existingRows.map((row) => row.blockNumber.trim().toLowerCase()),
  );

  let candidate = `${base} copy`;
  let counter = 2;
  while (existing.has(candidate.toLowerCase())) {
    candidate = `${base} copy ${counter}`;
    counter += 1;
  }
  return candidate;
}

function lshGetLongTermYearDefaults(): {
  projectedYear: string;
  historyYear1: string;
  historyYear2: string;
  historyYear3: string;
} {
  const currentYear = new Date().getFullYear();
  return {
    projectedYear: String(currentYear),
    historyYear1: String(currentYear - 1),
    historyYear2: String(currentYear - 2),
    historyYear3: String(currentYear - 3),
  };
}

function lshCreateEmptyLongTermRow(): IEditableLongTermRow {
  const defaults = lshGetLongTermYearDefaults();

  return {
    rowId: lshCreateLongTermRowId(),
    cropId: '',
    blockNumber: '',
    age: '',
    numberOfTrees: '',
    area: '',
    trellies: '',
    spacing: '',
    irrigation: '',
    type: '',
    projectedYear: defaults.projectedYear,
    projectedYield: '',
    projectedPrice: '',
    projectedArea: '',
    historyYear1: defaults.historyYear1,
    historyYield1: '',
    historyArea1: '',
    historyYear2: defaults.historyYear2,
    historyYield2: '',
    historyArea2: '',
    historyYear3: defaults.historyYear3,
    historyYield3: '',
    historyArea3: '',
    notes: '',
  };
}

function lshMapLongTermRecordToEditableRow(
  record: ILongTermCropRecord,
): IEditableLongTermRow {
  const defaults = lshGetLongTermYearDefaults();
  const persistedId =
    record?.id !== undefined && record?.id !== null
      ? Number(record.id)
      : undefined;

  return {
    rowId: lshCreateLongTermRowId(),
    id: Number.isFinite(persistedId as number) ? persistedId : undefined,
    persistedBlockNumber:
      lshToStringValue(record?.blockNumber).trim() || undefined,
    cropId: lshToStringValue(record?.crop?.id),
    blockNumber: lshToStringValue(record?.blockNumber),
    age: lshToStringValue(record?.age),
    numberOfTrees: lshToStringValue(record?.numberOfTrees),
    area: lshToStringValue(record?.area),
    trellies:
      record?.trellies === true
        ? 'YES'
        : record?.trellies === false
        ? 'NO'
        : '',
    spacing: lshToStringValue(record?.spacing),
    irrigation: lshToStringValue(record?.irrigation).toUpperCase(),
    type: lshToStringValue(record?.type),
    projectedYear:
      lshToStringValue(record?.projectedYear) || defaults.projectedYear,
    projectedYield: lshToStringValue(record?.projectedYield),
    projectedPrice: lshToStringValue(record?.projectedPrice),
    projectedArea: lshToStringValue(record?.projectedArea),
    historyYear1:
      lshToStringValue(record?.historyYear1) || defaults.historyYear1,
    historyYield1: lshToStringValue(record?.historyYield1),
    historyArea1: lshToStringValue(record?.historyArea1),
    historyYear2:
      lshToStringValue(record?.historyYear2) || defaults.historyYear2,
    historyYield2: lshToStringValue(record?.historyYield2),
    historyArea2: lshToStringValue(record?.historyArea2),
    historyYear3:
      lshToStringValue(record?.historyYear3) || defaults.historyYear3,
    historyYield3: lshToStringValue(record?.historyYield3),
    historyArea3: lshToStringValue(record?.historyArea3),
    notes: lshToStringValue(record?.notes),
  };
}

function lshGetExcelCellText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean')
    return String(value);
  if (value instanceof Date) return value.toISOString().slice(0, 10);

  if (typeof value === 'object') {
    const objectValue = value as {
      result?: unknown;
      text?: unknown;
      richText?: Array<{text?: unknown}>;
    };

    if (objectValue.result !== undefined && objectValue.result !== null) {
      return String(objectValue.result);
    }

    if (typeof objectValue.text === 'string') {
      return objectValue.text;
    }

    if (Array.isArray(objectValue.richText)) {
      return objectValue.richText
        .map((item) => String(item?.text || ''))
        .join('');
    }
  }

  return String(value);
}

function lshGetDynamicSteps(
  selectedGroups: CommodityGroupKey[],
): DynamicProductionStep[] {
  const steps: DynamicProductionStep[] = [
    {
      key: 'commodity-selection',
      title: 'Production commodities',
      description:
        'Select which commodities are included in the funding request.',
    },
  ];

  if (selectedGroups.includes('livestock')) {
    steps.push({
      key: 'livestock',
      title: 'Livestock',
      description:
        'Capture livestock groups, grading, quantities and sell-off details.',
    });
  }

  if (selectedGroups.includes('fruits')) {
    steps.push({
      key: 'long-term-crops',
      title: 'Long-term crops',
      description:
        'Capture orchard/block details with projected and historic performance.',
    });
  }

  if (
    selectedGroups.includes('vegetables') ||
    selectedGroups.includes('grains')
  ) {
    steps.push({
      key: 'production-plan',
      title: 'Production plan',
      description:
        'Capture short-term crop plans with hectares, timing and pricing.',
    });
    steps.push({
      key: 'short-term-production-history',
      title: 'Short-term history',
      description:
        'Capture past short-term crop performance to validate delivery record.',
    });
  }

  return steps;
}

const ApplicationProductionInfoStep: React.FC = () => {
  const {
    application,
    activeStep,
    activeSubStepIndex,
    goPrevious,
    goToStep,
    setApplication,
  } = useSupplierApplicationJourney();

  const [commodityTypes, setCommodityTypes] = useState<ICommodityType[]>([]);
  const [selectedCommodityIds, setSelectedCommodityIds] = useState<number[]>(
    [],
  );
  const [isLoadingCommodities, setIsLoadingCommodities] = useState(false);
  const [isSavingCommodities, setIsSavingCommodities] = useState(false);
  const [commodityError, setCommodityError] = useState<string | null>(null);

  useEffect(() => {
    if (!application?.id) {
      setCommodityTypes([]);
      setSelectedCommodityIds([]);
      setCommodityError(null);
      return;
    }

    let isMounted = true;
    setIsLoadingCommodities(true);
    setCommodityError(null);

    Promise.all([
      apiGetAllFdCommodityTypes(),
      apiGetFdApplicationHistoryCommoditiesByApplicationId(application.id),
    ])
      .then(([catalogue, selected]) => {
        if (!isMounted) return;

        setCommodityTypes(
          (Array.isArray(catalogue) ? catalogue : [])
            .map<ICommodityType | null>((row) => {
              const id = Number(row?.id);
              if (!Number.isFinite(id)) return null;

              return {
                id,
                name: String(row?.name || '').trim() || `Commodity ${id}`,
              };
            })
            .filter((commodity): commodity is ICommodityType =>
              Boolean(commodity),
            ),
        );

        setSelectedCommodityIds(
          (Array.isArray(selected) ? selected : [])
            .map((row) => Number(row?.commodityType?.id))
            .filter((id) => Number.isFinite(id)),
        );
      })
      .catch((error) => {
        if (!isMounted) return;

        setCommodityError(
          'Could not load commodity selections at the moment. Please try again.',
        );
        console.error(
          '[ApplicationProductionInfoStep] Failed to load commodity selections',
          error,
        );
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoadingCommodities(false);
      });

    return () => {
      isMounted = false;
    };
  }, [application?.id]);

  const [isLoadingLivestock, setIsLoadingLivestock] = useState(false);
  const [isSavingLivestock, setIsSavingLivestock] = useState(false);
  const [livestockError, setLivestockError] = useState<string | null>(null);
  const [livestockCatalogue, setLivestockCatalogue] = useState<
    ILivestockCatalogueItem[]
  >([]);
  const [livestockRecords, setLivestockRecords] = useState<
    IApplicationLivestockRecord[]
  >([]);
  const [livestockForm, setLivestockForm] =
    useState<ILivestockFormState>(EMPTY_LIVESTOCK_FORM);
  const [sellOffFrequency, setSellOffFrequency] =
    useState<SellOffFrequency>('not-selling');
  const [sellOffMonth, setSellOffMonth] = useState('');
  const [customSellOffValues, setCustomSellOffValues] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (!application?.id) {
      setLivestockCatalogue([]);
      setLivestockRecords([]);
      setLivestockError(null);
      return;
    }

    let isMounted = true;
    setIsLoadingLivestock(true);
    setLivestockError(null);

    Promise.all([
      apiGetAllFdLivestock(),
      apiGetFdApplicationLivestockByApplicationId(application.id),
    ])
      .then(([catalogueResponse, recordResponse]) => {
        if (!isMounted) return;

        const mappedCatalogue = (
          Array.isArray(catalogueResponse) ? catalogueResponse : []
        )
          .map<ILivestockCatalogueItem | null>((item) =>
            lshMapLivestockCatalogueItem(item),
          )
          .filter((item): item is ILivestockCatalogueItem => Boolean(item));

        const mappedRecords = (
          Array.isArray(recordResponse) ? recordResponse : []
        ).map((record) =>
          lshMapLivestockRecord(record, Number(application.id)),
        );

        setLivestockCatalogue(mappedCatalogue);
        setLivestockRecords(mappedRecords);
        setLivestockForm(EMPTY_LIVESTOCK_FORM);
        setSellOffFrequency('not-selling');
        setSellOffMonth('');
        setCustomSellOffValues({});
      })
      .catch((error) => {
        if (!isMounted) return;

        const fallbackRecords = (application?.applicationLivestock || []).map(
          (record) => lshMapLivestockRecord(record, Number(application.id)),
        );

        setLivestockRecords(fallbackRecords);
        setLivestockForm(EMPTY_LIVESTOCK_FORM);
        setSellOffFrequency('not-selling');
        setSellOffMonth('');
        setCustomSellOffValues({});
        setLivestockError(
          'Could not load livestock details at the moment. Please try again.',
        );
        console.error(
          '[ApplicationProductionInfoStep] Failed to load livestock details',
          error,
        );
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoadingLivestock(false);
      });

    return () => {
      isMounted = false;
    };
  }, [application?.id]);

  const [isLoadingLongTermCrops, setIsLoadingLongTermCrops] = useState(false);
  const [isSavingLongTermCrops, setIsSavingLongTermCrops] = useState(false);
  const [longTermCropOptions, setLongTermCropOptions] = useState<
    Array<{id: string; name: string}>
  >([]);
  const [longTermVarietyRecords, setLongTermVarietyRecords] = useState<
    IProduce[]
  >([]);
  const [longTermRows, setLongTermRows] = useState<IEditableLongTermRow[]>([]);
  const [longTermInitialRecords, setLongTermInitialRecords] = useState<
    ILongTermCropRecord[]
  >([]);
  const [longTermTableErrors, setLongTermTableErrors] =
    useState<ILongTermTableErrors>({});
  const [longTermTableErrorMessage, setLongTermTableErrorMessage] = useState<
    string | undefined
  >(undefined);
  const longTermUploadInputRef = useRef<HTMLInputElement | null>(null);

  const [isLoadingProductionPlans, setIsLoadingProductionPlans] =
    useState(false);
  const [isSavingProductionPlans, setIsSavingProductionPlans] = useState(false);
  const [hasAttemptedProductionPlanLoad, setHasAttemptedProductionPlanLoad] =
    useState(false);
  const [productionPlanCropOptions, setProductionPlanCropOptions] = useState<
    Array<{id: string; name: string}>
  >([]);
  const [productionPlanRows, setProductionPlanRows] = useState<
    IEditableProductionPlanRow[]
  >([]);
  const [productionPlanInitialRecords, setProductionPlanInitialRecords] =
    useState<IFdProductionPlan[]>([]);
  const [selectedProductionPlanCropToAdd, setSelectedProductionPlanCropToAdd] =
    useState('');
  const [productionPlanTableErrors, setProductionPlanTableErrors] =
    useState<IProductionPlanTableErrors>({});
  const [productionPlanTableErrorMessage, setProductionPlanTableErrorMessage] =
    useState<string | undefined>(undefined);

  const [isLoadingShortTermHistory, setIsLoadingShortTermHistory] =
    useState(false);
  const [
    hasAttemptedShortTermHistoryLoad,
    setHasAttemptedShortTermHistoryLoad,
  ] = useState(false);
  const [isSavingShortTermHistory, setIsSavingShortTermHistory] =
    useState(false);
  const [shortTermHistoryCropOptions, setShortTermHistoryCropOptions] =
    useState<Array<{id: string; name: string}>>([]);
  const [shortTermHistoryRows, setShortTermHistoryRows] = useState<
    IEditableHistoryRow[]
  >([]);
  const [shortTermHistoryInitialRecords, setShortTermHistoryInitialRecords] =
    useState<IFdProductionHistory[]>([]);
  const [
    selectedShortTermHistoryCropToAdd,
    setSelectedShortTermHistoryCropToAdd,
  ] = useState('');
  const [shortTermHistoryTableErrors, setShortTermHistoryTableErrors] =
    useState<IHistoryTableErrors>({});
  const [
    shortTermHistoryTableErrorMessage,
    setShortTermHistoryTableErrorMessage,
  ] = useState<string | undefined>(undefined);

  const {
    longTermCropNameById,
    longTermVarietyOptionsByCropId,
    longTermAllVarietyOptions,
    longTermVarietyLookupByCropId,
  } = lshUseLongTermCropsSubStep({
    longTermCropOptions,
    longTermInitialRecords,
    longTermVarietyRecords,
  });

  useEffect(() => {
    if (!application?.id) {
      setLongTermCropOptions([]);
      setLongTermVarietyRecords([]);
      setLongTermRows([]);
      setLongTermInitialRecords([]);
      setLongTermTableErrors({});
      setLongTermTableErrorMessage(undefined);
      return;
    }

    let isMounted = true;
    setIsLoadingLongTermCrops(true);
    setLongTermTableErrorMessage(undefined);

    Promise.all([
      apiGetAllFdCrops('LONG_TERM'),
      apiGetFdApplicationLongTermCropsByApplicationId(application.id),
      apiGetProduce().catch(() => []),
    ])
      .then(async ([cropsResponse, recordsResponse, varietiesResponse]) => {
        if (!isMounted) return;

        let crops = (Array.isArray(cropsResponse) ? cropsResponse : [])
          .map((crop) => ({
            id: String(crop?.id || '').trim(),
            name: String(crop?.name || '').trim(),
          }))
          .filter((crop) => crop.id && crop.name);

        if (crops.length === 0) {
          const fallbackCropsResponse = await apiGetAllFdCrops();
          crops = (
            Array.isArray(fallbackCropsResponse) ? fallbackCropsResponse : []
          )
            .map((crop) => ({
              id: String(crop?.id || '').trim(),
              name: String(crop?.name || '').trim(),
            }))
            .filter((crop) => crop.id && crop.name);
        }

        const records = Array.isArray(recordsResponse) ? recordsResponse : [];
        const varieties = Array.isArray(varietiesResponse)
          ? varietiesResponse
          : [];

        const cropNameLookup = new Map<string, string>();
        crops.forEach((crop) => {
          cropNameLookup.set(crop.id, crop.name);
        });
        records.forEach((record) => {
          const cropId = record?.crop?.id;
          if (cropId === undefined || cropId === null) return;
          const key = String(cropId);
          if (!cropNameLookup.has(key)) {
            cropNameLookup.set(
              key,
              String(record?.crop?.name || '').trim() || 'Unknown crop',
            );
          }
        });

        const mappedRows = records
          .map((record) => lshMapLongTermRecordToEditableRow(record))
          .sort((left, right) => {
            const cropLeft = (
              cropNameLookup.get(left.cropId) || ''
            ).toLowerCase();
            const cropRight = (
              cropNameLookup.get(right.cropId) || ''
            ).toLowerCase();
            if (cropLeft !== cropRight)
              return cropLeft.localeCompare(cropRight);
            return left.blockNumber
              .toLowerCase()
              .localeCompare(right.blockNumber.toLowerCase());
          });

        setLongTermCropOptions(
          Array.from(cropNameLookup.entries())
            .map(([id, name]) => ({id, name}))
            .sort((left, right) =>
              left.name.toLowerCase().localeCompare(right.name.toLowerCase()),
            ),
        );
        setLongTermInitialRecords(records);
        setLongTermVarietyRecords(varieties);
        setLongTermRows(mappedRows);
        setLongTermTableErrors({});
        setLongTermTableErrorMessage(undefined);
      })
      .catch((error) => {
        if (!isMounted) return;

        console.error(
          '[ApplicationProductionInfoStep] Failed to load long-term crops',
          error,
        );

        const fallbackRecords = (
          application?.applicationLongTermCrops || []
        ).map((record) =>
          lshMapLongTermRecordToEditableRow(
            record as unknown as ILongTermCropRecord,
          ),
        );
        setLongTermRows(fallbackRecords);
        setLongTermInitialRecords(
          (application?.applicationLongTermCrops ||
            []) as unknown as ILongTermCropRecord[],
        );
        setLongTermTableErrors({});
        setLongTermTableErrorMessage(
          "We couldn't load long-term crop records. Please try again.",
        );
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoadingLongTermCrops(false);
      });

    return () => {
      isMounted = false;
    };
  }, [application?.applicationLongTermCrops, application?.id]);

  const {productionPlanCropNameById, addableProductionPlanCropOptions} =
    lshUseProductionPlanSubStep({
      productionPlanCropOptions,
      productionPlanInitialRecords,
      productionPlanRows,
    });

  useEffect(() => {
    if (!selectedProductionPlanCropToAdd) return;

    const exists = addableProductionPlanCropOptions.some(
      (option) => option.id === selectedProductionPlanCropToAdd,
    );
    if (!exists) {
      setSelectedProductionPlanCropToAdd('');
    }
  }, [addableProductionPlanCropOptions, selectedProductionPlanCropToAdd]);

  useEffect(() => {
    if (!application?.id) {
      setIsLoadingProductionPlans(false);
      setHasAttemptedProductionPlanLoad(false);
      setProductionPlanCropOptions([]);
      setProductionPlanRows([]);
      setProductionPlanInitialRecords([]);
      setSelectedProductionPlanCropToAdd('');
      setProductionPlanTableErrors({});
      setProductionPlanTableErrorMessage(undefined);
      return;
    }

    let isMounted = true;
    setIsLoadingProductionPlans(true);
    setProductionPlanTableErrors({});
    setProductionPlanTableErrorMessage(undefined);

    Promise.all([
      apiGetAllFdCrops('SHORT_TERM'),
      apiGetFdProductionPlansByApplicationId(application.id),
    ])
      .then(async ([cropResponse, planResponse]) => {
        if (!isMounted) return;

        let crops = (Array.isArray(cropResponse) ? cropResponse : [])
          .map((item) => ({
            id: String(item?.id || '').trim(),
            name: String(item?.name || '').trim(),
          }))
          .filter((crop) => crop.id && crop.name);

        if (crops.length === 0) {
          const fallbackCropResponse = await apiGetAllFdCrops();
          crops = (
            Array.isArray(fallbackCropResponse) ? fallbackCropResponse : []
          )
            .map((item) => ({
              id: String(item?.id || '').trim(),
              name: String(item?.name || '').trim(),
            }))
            .filter((crop) => crop.id && crop.name);
        }

        const plans = Array.isArray(planResponse) ? planResponse : [];
        const cropNameLookup = new Map<string, string>();
        crops.forEach((crop) => {
          cropNameLookup.set(crop.id, crop.name);
        });
        plans.forEach((record) => {
          const cropIdRaw = record?.crop?.id;
          if (cropIdRaw === undefined || cropIdRaw === null) return;
          const key = String(cropIdRaw);
          if (!cropNameLookup.has(key)) {
            cropNameLookup.set(
              key,
              String(record?.crop?.name || '').trim() || 'Unknown crop',
            );
          }
        });

        const mappedRows = plans
          .map((record) => lshMapProductionPlanRecordToEditableRow(record))
          .filter((row): row is IEditableProductionPlanRow => Boolean(row));

        const uniqueByCrop = new Map<string, IEditableProductionPlanRow>();
        [...mappedRows]
          .sort((left, right) => Number(right.id || 0) - Number(left.id || 0))
          .forEach((row) => {
            if (!uniqueByCrop.has(row.cropId)) {
              uniqueByCrop.set(row.cropId, row);
            }
          });

        const normalizedCropOptions = Array.from(cropNameLookup.entries())
          .map(([id, name]) => ({id, name}))
          .sort((left, right) =>
            left.name.toLowerCase().localeCompare(right.name.toLowerCase()),
          );

        const normalizedRows = Array.from(uniqueByCrop.values()).sort(
          (left, right) => {
            const nameLeft = cropNameLookup.get(left.cropId) || '';
            const nameRight = cropNameLookup.get(right.cropId) || '';
            return nameLeft
              .toLowerCase()
              .localeCompare(nameRight.toLowerCase());
          },
        );

        setProductionPlanCropOptions(normalizedCropOptions);
        setProductionPlanInitialRecords(plans);
        setProductionPlanRows(normalizedRows);
        setProductionPlanTableErrors({});
        setProductionPlanTableErrorMessage(undefined);
      })
      .catch((error) => {
        if (!isMounted) return;

        console.error(
          '[ApplicationProductionInfoStep] Failed to load production plans',
          error,
        );

        const fallbackPlans = (
          Array.isArray(application?.productionPlans)
            ? application.productionPlans
            : []
        ) as IFdProductionPlan[];
        const fallbackRows = fallbackPlans
          .map((record) => lshMapProductionPlanRecordToEditableRow(record))
          .filter((row): row is IEditableProductionPlanRow => Boolean(row));

        setProductionPlanCropOptions([]);
        setProductionPlanRows(fallbackRows);
        setProductionPlanInitialRecords(fallbackPlans);
        setProductionPlanTableErrors({});
        setProductionPlanTableErrorMessage(
          "We couldn't load production plans. Please try again.",
        );
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoadingProductionPlans(false);
        setHasAttemptedProductionPlanLoad(true);
      });

    return () => {
      isMounted = false;
    };
  }, [application?.id, application?.productionPlans]);

  const {
    requiredShortTermHistoryYears,
    shortTermHistoryGroupedRows,
    addableShortTermHistoryCropOptions,
  } = lshUseShortTermHistorySubStep({
    shortTermHistoryCropOptions,
    shortTermHistoryInitialRecords,
    shortTermHistoryRows,
  });

  useEffect(() => {
    if (!selectedShortTermHistoryCropToAdd) return;

    const exists = addableShortTermHistoryCropOptions.some(
      (option) => option.id === selectedShortTermHistoryCropToAdd,
    );
    if (!exists) {
      setSelectedShortTermHistoryCropToAdd('');
    }
  }, [addableShortTermHistoryCropOptions, selectedShortTermHistoryCropToAdd]);

  useEffect(() => {
    if (!application?.id) {
      setIsLoadingShortTermHistory(false);
      setHasAttemptedShortTermHistoryLoad(false);
      setShortTermHistoryCropOptions([]);
      setShortTermHistoryRows([]);
      setShortTermHistoryInitialRecords([]);
      setSelectedShortTermHistoryCropToAdd('');
      setShortTermHistoryTableErrors({});
      setShortTermHistoryTableErrorMessage(undefined);
      return;
    }

    let isMounted = true;
    setIsLoadingShortTermHistory(true);
    setShortTermHistoryTableErrors({});
    setShortTermHistoryTableErrorMessage(undefined);

    Promise.all([
      apiGetAllFdCrops('SHORT_TERM'),
      apiGetFdProductionHistoryByApplicationId(application.id),
    ])
      .then(async ([cropResponse, historyResponse]) => {
        if (!isMounted) return;

        let crops = (Array.isArray(cropResponse) ? cropResponse : [])
          .map((item) => ({
            id: String(item?.id || '').trim(),
            name: String(item?.name || '').trim(),
          }))
          .filter((crop) => crop.id && crop.name);

        if (crops.length === 0) {
          const fallbackCropResponse = await apiGetAllFdCrops();
          crops = (
            Array.isArray(fallbackCropResponse) ? fallbackCropResponse : []
          )
            .map((item) => ({
              id: String(item?.id || '').trim(),
              name: String(item?.name || '').trim(),
            }))
            .filter((crop) => crop.id && crop.name);
        }

        const records = (
          Array.isArray(historyResponse) ? historyResponse : []
        ) as IFdProductionHistory[];

        const cropNameLookup = new Map<string, string>();
        crops.forEach((crop) => {
          cropNameLookup.set(crop.id, crop.name);
        });
        records.forEach((record) => {
          const cropIdRaw = record?.crop?.id;
          if (cropIdRaw === undefined || cropIdRaw === null) return;
          const key = String(cropIdRaw);
          if (!cropNameLookup.has(key)) {
            cropNameLookup.set(
              key,
              String(record?.crop?.name || '').trim() || 'Unknown crop',
            );
          }
        });

        const recordByCropYear = new Map<string, IFdProductionHistory>();
        const recordCropIds = new Set<string>();

        [...records]
          .sort((left, right) => Number(right?.id || 0) - Number(left?.id || 0))
          .forEach((record) => {
            const cropIdRaw = record?.crop?.id;
            const yearValue = Number(record?.year ?? NaN);
            if (cropIdRaw === undefined || cropIdRaw === null) return;

            const cropId = String(cropIdRaw);
            recordCropIds.add(cropId);

            if (!Number.isFinite(yearValue)) return;
            const key = `${cropId}-${yearValue}`;
            if (!recordByCropYear.has(key)) {
              recordByCropYear.set(key, record);
            }
          });

        const sortedCropIds = Array.from(recordCropIds).sort((left, right) => {
          const nameLeft = (cropNameLookup.get(left) || '').toLowerCase();
          const nameRight = (cropNameLookup.get(right) || '').toLowerCase();
          return nameLeft.localeCompare(nameRight);
        });

        const normalizedRows = sortedCropIds.flatMap((cropId) =>
          requiredShortTermHistoryYears.map((year) => {
            const existing = recordByCropYear.get(`${cropId}-${year}`);
            return existing
              ? lshMapProductionHistoryRecordToEditableRow(
                  existing,
                  cropId,
                  year,
                )
              : lshCreateEmptyHistoryRow(cropId, year);
          }),
        );

        const normalizedCropOptions = Array.from(cropNameLookup.entries())
          .map(([id, name]) => ({id, name}))
          .sort((left, right) =>
            left.name.toLowerCase().localeCompare(right.name.toLowerCase()),
          );

        setShortTermHistoryCropOptions(normalizedCropOptions);
        setShortTermHistoryInitialRecords(records);
        setShortTermHistoryRows(normalizedRows);
        setShortTermHistoryTableErrors({});
        setShortTermHistoryTableErrorMessage(undefined);
      })
      .catch((error) => {
        if (!isMounted) return;

        console.error(
          '[ApplicationProductionInfoStep] Failed to load short-term history',
          error,
        );

        const fallbackRecords = (
          Array.isArray(application?.productionHistory)
            ? application.productionHistory
            : []
        ) as IFdProductionHistory[];
        const fallbackCropNameLookup = new Map<string, string>();
        fallbackRecords.forEach((record) => {
          const cropIdRaw = record?.crop?.id;
          if (cropIdRaw === undefined || cropIdRaw === null) return;
          fallbackCropNameLookup.set(
            String(cropIdRaw),
            String(record?.crop?.name || '').trim() || 'Unknown crop',
          );
        });

        const recordByCropYear = new Map<string, IFdProductionHistory>();
        const recordCropIds = new Set<string>();
        [...fallbackRecords]
          .sort((left, right) => Number(right?.id || 0) - Number(left?.id || 0))
          .forEach((record) => {
            const cropIdRaw = record?.crop?.id;
            const yearValue = Number(record?.year ?? NaN);
            if (cropIdRaw === undefined || cropIdRaw === null) return;
            const cropId = String(cropIdRaw);
            recordCropIds.add(cropId);
            if (!Number.isFinite(yearValue)) return;

            const key = `${cropId}-${yearValue}`;
            if (!recordByCropYear.has(key)) {
              recordByCropYear.set(key, record);
            }
          });

        const sortedCropIds = Array.from(recordCropIds).sort((left, right) => {
          const nameLeft = (
            fallbackCropNameLookup.get(left) || ''
          ).toLowerCase();
          const nameRight = (
            fallbackCropNameLookup.get(right) || ''
          ).toLowerCase();
          return nameLeft.localeCompare(nameRight);
        });

        const fallbackRows = sortedCropIds.flatMap((cropId) =>
          requiredShortTermHistoryYears.map((year) => {
            const existing = recordByCropYear.get(`${cropId}-${year}`);
            return existing
              ? lshMapProductionHistoryRecordToEditableRow(
                  existing,
                  cropId,
                  year,
                )
              : lshCreateEmptyHistoryRow(cropId, year);
          }),
        );

        setShortTermHistoryCropOptions(
          Array.from(fallbackCropNameLookup.entries())
            .map(([id, name]) => ({id, name}))
            .sort((left, right) =>
              left.name.toLowerCase().localeCompare(right.name.toLowerCase()),
            ),
        );
        setShortTermHistoryRows(fallbackRows);
        setShortTermHistoryInitialRecords(fallbackRecords);
        setShortTermHistoryTableErrors({});
        setShortTermHistoryTableErrorMessage(
          "We couldn't load production history. Please try again.",
        );
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoadingShortTermHistory(false);
        setHasAttemptedShortTermHistoryLoad(true);
      });

    return () => {
      isMounted = false;
    };
  }, [
    application?.id,
    application?.productionHistory,
    requiredShortTermHistoryYears,
  ]);

  const {selectedCommoditySet, handleToggleCommodity} =
    lshUseCommoditySelectionSubStep({
      selectedCommodityIds,
      isSavingCommodities,
      setCommodityError,
      setSelectedCommodityIds,
    });

  const {
    onceOffMonths,
    customMonths,
    livestockSelectOptions,
    livestockTypeOptions,
    handleLivestockFormChange,
    handleSellOffFrequencyChange,
    handleSellOffMonthChange,
    handleCustomSellOffValueChange,
    handleAddLivestockRecord,
    handleRemoveLivestockRecord,
    handleSaveLivestockStep,
  } = lshUseLivestockSubStep({
    applicationId: application?.id ? Number(application.id) : undefined,
    isSavingLivestock,
    livestockCatalogue,
    livestockRecords,
    livestockForm,
    sellOffFrequency,
    sellOffMonth,
    customSellOffValues,
    setLivestockError,
    setIsSavingLivestock,
    setLivestockForm,
    setSellOffFrequency,
    setSellOffMonth,
    setCustomSellOffValues,
    setLivestockRecords,
    mapLivestockRecord: lshMapLivestockRecord,
  });

  const selectedGroups = useMemo(() => {
    const groups = new Set<CommodityGroupKey>();

    commodityTypes.forEach((commodity) => {
      if (!selectedCommoditySet.has(Number(commodity.id))) {
        return;
      }

      const mappedGroup =
        COMMODITY_GROUP_BY_NAME[
          String(commodity.name || '')
            .trim()
            .toLowerCase()
        ];
      if (!mappedGroup) return;

      groups.add(mappedGroup);
    });

    return Array.from(groups);
  }, [commodityTypes, selectedCommoditySet]);

  const dynamicSteps = useMemo(
    () => lshGetDynamicSteps(selectedGroups),
    [selectedGroups],
  );

  const requestedSubStepKey = useMemo<ProductionSubStepKey>(() => {
    const subStepKey =
      activeStep.subSteps?.[activeSubStepIndex]?.key || 'commodity-selection';
    return subStepKey as ProductionSubStepKey;
  }, [activeStep.subSteps, activeSubStepIndex]);

  const activeDynamicIndex = useMemo(() => {
    const index = dynamicSteps.findIndex(
      (step) => step.key === requestedSubStepKey,
    );
    return index >= 0 ? index : 0;
  }, [dynamicSteps, requestedSubStepKey]);

  const activeDynamicStep = dynamicSteps[activeDynamicIndex] || dynamicSteps[0];

  useEffect(() => {
    if (!activeDynamicStep) return;
    if (dynamicSteps.some((step) => step.key === requestedSubStepKey)) return;

    goToStep('production-info', {subStepKey: dynamicSteps[0].key});
  }, [activeDynamicStep, dynamicSteps, goToStep, requestedSubStepKey]);

  const goToNextDynamicStep = useCallback(() => {
    const nextDynamicStep = dynamicSteps[activeDynamicIndex + 1];
    if (nextDynamicStep) {
      goToStep('production-info', {subStepKey: nextDynamicStep.key});
      return;
    }

    goToStep('financials', {subStepKey: 'financial-summary'});
  }, [activeDynamicIndex, dynamicSteps, goToStep]);

  const goToPreviousDynamicStep = useCallback(() => {
    if (activeDynamicIndex > 0) {
      const previousDynamicStep = dynamicSteps[activeDynamicIndex - 1];
      if (previousDynamicStep) {
        goToStep('production-info', {subStepKey: previousDynamicStep.key});
        return;
      }
    }

    goPrevious();
  }, [activeDynamicIndex, dynamicSteps, goPrevious, goToStep]);

  const handleAddLongTermRow = useCallback(() => {
    setLongTermRows((current) => [...current, lshCreateEmptyLongTermRow()]);
    setLongTermTableErrorMessage(undefined);
  }, []);

  const handleDuplicateLongTermRow = useCallback((rowId: string) => {
    setLongTermRows((current) => {
      const source = current.find((row) => row.rowId === rowId);
      if (!source) return current;

      const duplicate: IEditableLongTermRow = {
        ...source,
        rowId: lshCreateLongTermRowId(),
        id: undefined,
        persistedBlockNumber: undefined,
        blockNumber: lshGetNextDuplicateBlockNumber(
          source.blockNumber,
          current,
        ),
      };

      return [...current, duplicate];
    });
    setLongTermTableErrorMessage(undefined);
  }, []);

  const handleRemoveLongTermRow = useCallback((rowId: string) => {
    setLongTermRows((current) => current.filter((row) => row.rowId !== rowId));
    setLongTermTableErrors((current) => {
      if (!current[rowId]) return current;
      const next = {...current};
      delete next[rowId];
      return next;
    });
    setLongTermTableErrorMessage(undefined);
  }, []);

  const handleUpdateLongTermRowField = useCallback(
    (rowId: string, field: IEditableLongTermField, value: string) => {
      setLongTermRows((current) =>
        current.map((row) => {
          if (row.rowId !== rowId) return row;

          if (field === 'cropId') {
            return {
              ...row,
              cropId: value,
              type: '',
            };
          }

          return {
            ...row,
            [field]: value,
          };
        }),
      );

      setLongTermTableErrors((current) => {
        const rowErrors = current[rowId];
        if (!rowErrors || (!rowErrors[field] && !rowErrors.general))
          return current;

        const next = {...current};
        const nextRowErrors = {...rowErrors};
        delete nextRowErrors[field];
        delete nextRowErrors.general;

        if (Object.keys(nextRowErrors).length === 0) {
          delete next[rowId];
        } else {
          next[rowId] = nextRowErrors;
        }

        return next;
      });

      setLongTermTableErrorMessage(undefined);
    },
    [],
  );

  const handleDownloadLongTermTemplate = useCallback(async () => {
    try {
      const ExcelJSModule = await import('exceljs');
      const ExcelJS = ExcelJSModule.default;
      const exportRows =
        longTermRows.length > 0 ? longTermRows : [lshCreateEmptyLongTermRow()];
      const templateRowCount = Math.max(
        exportRows.length + 25,
        LONG_TERM_TEMPLATE_MIN_ROW_COUNT,
      );

      const cropNames = longTermCropOptions
        .map((crop) => crop.name.trim())
        .filter((value) => Boolean(value));
      const trelliesValues = lshGetOptionListValues(
        LONG_TERM_TRELLIES_OPTIONS,
        'label',
      );
      const irrigationValues = lshGetOptionListValues(
        LONG_TERM_IRRIGATION_OPTIONS,
        'label',
      );
      const varietyValues = longTermAllVarietyOptions;

      const workbook = new ExcelJS.Workbook();
      const dataSheet = workbook.addWorksheet(LONG_TERM_TEMPLATE_SHEET_NAME);
      const lookupSheet = workbook.addWorksheet(LONG_TERM_LOOKUP_SHEET_NAME, {
        state: 'veryHidden',
      });

      const headers = LONG_TERM_TEMPLATE_COLUMNS.map((column) => column.header);
      dataSheet.addRow(headers);
      dataSheet.getRow(1).font = {bold: true};
      dataSheet.views = [{state: 'frozen', ySplit: 1}];
      dataSheet.autoFilter = {
        from: {row: 1, column: 1},
        to: {row: 1, column: LONG_TERM_TEMPLATE_COLUMNS.length},
      };

      dataSheet.columns = headers.map((header) => ({
        width: Math.max(14, Math.min(34, header.length + 3)),
      }));

      exportRows.forEach((row) => {
        const values = LONG_TERM_TEMPLATE_COLUMNS.map((column) => {
          if (column.key === 'cropId') {
            return longTermCropNameById.get(row.cropId) || row.cropId;
          }
          if (column.key === 'trellies') {
            return lshGetOptionLabel(LONG_TERM_TRELLIES_OPTIONS, row.trellies);
          }
          if (column.key === 'irrigation') {
            return lshGetOptionLabel(
              LONG_TERM_IRRIGATION_OPTIONS,
              row.irrigation,
            );
          }
          return row[column.key] || '';
        });
        dataSheet.addRow(values);
      });

      const lookupColumns: Array<{
        key: 'cropId' | 'type' | 'irrigation' | 'trellies';
        values: string[];
      }> = [
        {key: 'cropId', values: cropNames},
        {key: 'type', values: varietyValues},
        {key: 'irrigation', values: irrigationValues},
        {key: 'trellies', values: trelliesValues},
      ];

      lookupColumns.forEach((column, index) => {
        const columnIndex = index + 1;
        lookupSheet.getCell(1, columnIndex).value = column.key;
        const values = column.values.length > 0 ? column.values : [''];
        values.forEach((value, rowIndex) => {
          lookupSheet.getCell(rowIndex + 2, columnIndex).value = value;
        });
      });

      const columnIndexByKey = new Map<IEditableLongTermField, number>();
      LONG_TERM_TEMPLATE_COLUMNS.forEach((column, index) => {
        columnIndexByKey.set(column.key, index + 1);
      });

      const validationColumns: Array<{
        key: IEditableLongTermField;
        lookupColumnIndex: number;
        valuesCount: number;
      }> = [
        {
          key: 'cropId',
          lookupColumnIndex: 1,
          valuesCount: Math.max(1, cropNames.length),
        },
        {
          key: 'type',
          lookupColumnIndex: 2,
          valuesCount: Math.max(1, varietyValues.length),
        },
        {
          key: 'irrigation',
          lookupColumnIndex: 3,
          valuesCount: Math.max(1, irrigationValues.length),
        },
        {
          key: 'trellies',
          lookupColumnIndex: 4,
          valuesCount: Math.max(1, trelliesValues.length),
        },
      ];

      validationColumns.forEach(({key, lookupColumnIndex, valuesCount}) => {
        const targetColumn = columnIndexByKey.get(key);
        if (!targetColumn) return;

        const lookupColumn = lshGetExcelColumnName(lookupColumnIndex);
        const listRange =
          `${LONG_TERM_LOOKUP_SHEET_NAME}!$${lookupColumn}$2:$${lookupColumn}$` +
          String(valuesCount + 1);

        for (
          let rowIndex = 2;
          rowIndex <= templateRowCount + 1;
          rowIndex += 1
        ) {
          dataSheet.getCell(rowIndex, targetColumn).dataValidation = {
            type: 'list',
            allowBlank: true,
            formulae: [listRange],
            showErrorMessage: true,
            errorStyle: 'error',
            errorTitle: 'Invalid option',
            error: 'Select a value from the predefined list.',
          };
        }
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const fileBlob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(fileBlob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'long-term-crops-template.xlsx';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(
        '[ApplicationProductionInfoStep] Failed to generate long-term crop template',
        error,
      );
      setLongTermTableErrorMessage(
        "We couldn't generate the Excel template. Please try again.",
      );
    }
  }, [
    longTermAllVarietyOptions,
    longTermCropNameById,
    longTermCropOptions,
    longTermRows,
  ]);

  const handleOpenLongTermTemplatePicker = useCallback(() => {
    longTermUploadInputRef.current?.click();
  }, []);

  const handleLongTermTemplateUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.currentTarget;
      const file = input.files?.[0];
      if (!file) return;

      const lowerName = file.name.toLowerCase();
      const isExcelFile =
        lowerName.endsWith('.xlsx') ||
        lowerName.endsWith('.xls') ||
        file.type.includes('spreadsheetml') ||
        file.type.includes('ms-excel');

      if (!isExcelFile) {
        setLongTermTableErrorMessage(
          'Please upload an Excel template file (.xlsx or .xls).',
        );
        input.value = '';
        return;
      }

      try {
        const fileContent = await new Promise<ArrayBuffer>(
          (resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              if (reader.result instanceof ArrayBuffer) {
                resolve(reader.result);
                return;
              }
              reject(new Error('Invalid file data.'));
            };
            reader.onerror = () => {
              reject(
                new Error(
                  "We couldn't read the uploaded file. Please try again.",
                ),
              );
            };
            reader.readAsArrayBuffer(file);
          },
        );

        const ExcelJSModule = await import('exceljs');
        const workbook = new ExcelJSModule.Workbook();
        await workbook.xlsx.load(fileContent);

        const worksheet = workbook.worksheets[0];
        if (!worksheet) {
          throw new Error('The uploaded workbook has no sheets.');
        }

        const maxColumnCount = Math.max(
          LONG_TERM_TEMPLATE_COLUMNS.length,
          worksheet.columnCount,
        );
        const parsedRows: string[][] = [];
        for (let rowIndex = 1; rowIndex <= worksheet.rowCount; rowIndex += 1) {
          const row = worksheet.getRow(rowIndex);
          const values = Array.from(
            {length: maxColumnCount},
            (_, columnIndex) =>
              lshGetExcelCellText(row.getCell(columnIndex + 1).value),
          );
          parsedRows.push(values);
        }

        if (parsedRows.length < 2) {
          throw new Error('The uploaded template has no data rows.');
        }

        const headerRow = parsedRows[0].map((cell) => String(cell || ''));
        const columnIndexByField: Partial<
          Record<IEditableLongTermField, number>
        > = {};

        headerRow.forEach((header, index) => {
          const key =
            LONG_TERM_TEMPLATE_HEADER_ALIASES[
              lshNormaliseHeader(String(header || ''))
            ];
          if (key !== undefined && columnIndexByField[key] === undefined) {
            columnIndexByField[key] = index;
          }
        });

        const requiredFields: IEditableLongTermField[] = [
          'cropId',
          'blockNumber',
        ];
        const missingRequired = requiredFields.filter(
          (field) => columnIndexByField[field] === undefined,
        );

        if (missingRequired.length > 0) {
          throw new Error(
            `Template is missing required columns: ${missingRequired.join(
              ', ',
            )}.`,
          );
        }

        const cropIdSet = new Set(longTermCropOptions.map((crop) => crop.id));
        const cropIdByName = new Map(
          longTermCropOptions.map((crop) => [
            crop.name.trim().toLowerCase(),
            crop.id,
          ]),
        );
        const allowedIrrigationValues = new Set(
          lshGetOptionListValues(LONG_TERM_IRRIGATION_OPTIONS, 'value'),
        );

        const existingByCropBlock = new Map<string, IEditableLongTermRow>();
        longTermRows.forEach((row) => {
          const cropId = row.cropId.trim();
          const block = row.blockNumber.trim().toLowerCase();
          if (!cropId || !block) return;
          existingByCropBlock.set(`${cropId}::${block}`, row);
        });

        const importedRows: IEditableLongTermRow[] = [];
        const importErrors: string[] = [];

        for (let rowIndex = 1; rowIndex < parsedRows.length; rowIndex += 1) {
          const sourceRow = parsedRows[rowIndex];

          const getFieldValue = (field: IEditableLongTermField) => {
            const columnIndex = columnIndexByField[field];
            if (columnIndex === undefined) return '';
            return String(sourceRow[columnIndex] || '').trim();
          };

          const hasAnyValue = LONG_TERM_TEMPLATE_COLUMNS.some((column) =>
            Boolean(getFieldValue(column.key)),
          );
          if (!hasAnyValue) continue;

          const cropValueRaw = getFieldValue('cropId');
          const blockNumber = getFieldValue('blockNumber');

          if (!cropValueRaw || !blockNumber) {
            importErrors.push(
              `Row ${rowIndex + 1}: Crop and block number are required.`,
            );
            continue;
          }

          const cropValueNormalised = cropValueRaw.toLowerCase();
          const resolvedCropId = cropIdSet.has(cropValueRaw)
            ? cropValueRaw
            : cropIdByName.get(cropValueNormalised) || '';

          if (!resolvedCropId) {
            importErrors.push(
              `Row ${
                rowIndex + 1
              }: Unknown crop "${cropValueRaw}". Use a crop name from the system.`,
            );
            continue;
          }

          const matchKey = `${resolvedCropId}::${blockNumber.toLowerCase()}`;
          const existingRow = existingByCropBlock.get(matchKey);

          const trelliesRaw = getFieldValue('trellies');
          const trellies = lshNormaliseTrelliesValue(trelliesRaw);
          if (trelliesRaw && !trellies) {
            importErrors.push(
              `Row ${
                rowIndex + 1
              }: Invalid trellies "${trelliesRaw}". Use Yes or No.`,
            );
            continue;
          }

          const irrigationRaw = getFieldValue('irrigation');
          const irrigation = lshNormaliseIrrigationValue(irrigationRaw);
          if (irrigationRaw && !allowedIrrigationValues.has(irrigation)) {
            importErrors.push(
              `Row ${
                rowIndex + 1
              }: Invalid irrigation "${irrigationRaw}". Use a predefined irrigation option.`,
            );
            continue;
          }

          const varietyRaw = getFieldValue('type');
          let variety = '';
          if (varietyRaw) {
            const varietyLookup =
              longTermVarietyLookupByCropId.get(resolvedCropId) ||
              new Map<string, string>();
            const resolvedVariety = varietyLookup.get(varietyRaw.toLowerCase());
            if (!resolvedVariety) {
              importErrors.push(
                `Row ${
                  rowIndex + 1
                }: Invalid crop variety "${varietyRaw}" for the selected crop.`,
              );
              continue;
            }
            variety = resolvedVariety;
          }

          const nextRow: IEditableLongTermRow = {
            ...lshCreateEmptyLongTermRow(),
            rowId: lshCreateLongTermRowId(),
            id: existingRow?.id,
            persistedBlockNumber: existingRow?.persistedBlockNumber,
            cropId: resolvedCropId,
            blockNumber,
            age: getFieldValue('age'),
            numberOfTrees: getFieldValue('numberOfTrees'),
            area: getFieldValue('area'),
            trellies,
            spacing: getFieldValue('spacing'),
            irrigation,
            type: variety,
            projectedYear: getFieldValue('projectedYear'),
            projectedYield: getFieldValue('projectedYield'),
            projectedPrice: getFieldValue('projectedPrice'),
            projectedArea: getFieldValue('projectedArea'),
            historyYear1: getFieldValue('historyYear1'),
            historyYield1: getFieldValue('historyYield1'),
            historyArea1: getFieldValue('historyArea1'),
            historyYear2: getFieldValue('historyYear2'),
            historyYield2: getFieldValue('historyYield2'),
            historyArea2: getFieldValue('historyArea2'),
            historyYear3: getFieldValue('historyYear3'),
            historyYield3: getFieldValue('historyYield3'),
            historyArea3: getFieldValue('historyArea3'),
            notes: getFieldValue('notes'),
          };

          importedRows.push(nextRow);
        }

        if (importErrors.length > 0) {
          throw new Error(importErrors.slice(0, 4).join(' '));
        }

        if (importedRows.length === 0) {
          throw new Error('No valid rows found in the uploaded template.');
        }

        setLongTermRows(importedRows);
        setLongTermTableErrors({});
        setLongTermTableErrorMessage(undefined);
      } catch (error: any) {
        setLongTermTableErrorMessage(
          error?.message ||
            "We couldn't import the template. Please check the file format.",
        );
      } finally {
        input.value = '';
      }
    },
    [
      longTermCropOptions,
      longTermRows,
      longTermVarietyLookupByCropId,
      setLongTermRows,
    ],
  );

  const handleValidateLongTermRows = useCallback((): boolean => {
    const nextErrors: ILongTermTableErrors = {};

    longTermRows.forEach((row) => {
      const rowErrors: ILongTermRowErrors = {};

      if (!row.cropId.trim()) {
        rowErrors.cropId = 'Select a crop.';
      }

      if (!row.blockNumber.trim()) {
        rowErrors.blockNumber = 'Enter a block number.';
      }

      LONG_TERM_NUMERIC_FIELDS.forEach((field) => {
        const value = row[field];
        if (!value.trim()) return;
        if (!Number.isFinite(Number(value))) {
          rowErrors[field] = 'Use a numeric value.';
        }
      });

      if (Object.keys(rowErrors).length > 0) {
        nextErrors[row.rowId] = rowErrors;
      }
    });

    const seenByCropAndBlock = new Map<string, string>();
    longTermRows.forEach((row) => {
      const cropId = row.cropId.trim();
      const block = row.blockNumber.trim().toLowerCase();
      if (!cropId || !block) return;
      const dedupeKey = `${cropId}::${block}`;
      const existingRowId = seenByCropAndBlock.get(dedupeKey);
      if (!existingRowId) {
        seenByCropAndBlock.set(dedupeKey, row.rowId);
        return;
      }

      nextErrors[row.rowId] = {
        ...(nextErrors[row.rowId] || {}),
        general: 'Duplicate crop + block rows are not allowed.',
      };
      nextErrors[existingRowId] = {
        ...(nextErrors[existingRowId] || {}),
        general: 'Duplicate crop + block rows are not allowed.',
      };
    });

    setLongTermTableErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setLongTermTableErrorMessage(
        'Please fix highlighted row errors before saving.',
      );
      return false;
    }

    setLongTermTableErrorMessage(undefined);
    return true;
  }, [longTermRows]);

  const handleSaveLongTermCropsStep =
    useCallback(async (): Promise<boolean> => {
      if (!application?.id || isSavingLongTermCrops || isLoadingLongTermCrops) {
        return false;
      }

      if (longTermRows.length === 0) {
        setLongTermTableErrorMessage(
          'Add at least one block before continuing.',
        );
        return false;
      }

      if (!handleValidateLongTermRows()) {
        return false;
      }

      try {
        setIsSavingLongTermCrops(true);

        const initialById = new Map<number, ILongTermCropRecord>();
        [...longTermInitialRecords]
          .sort((left, right) => Number(right?.id || 0) - Number(left?.id || 0))
          .forEach((record) => {
            const id =
              record?.id !== undefined && record?.id !== null
                ? Number(record.id)
                : NaN;
            if (Number.isFinite(id) && !initialById.has(id)) {
              initialById.set(id, record);
            }
          });

        const currentIds = new Set<number>(
          longTermRows
            .map((row) =>
              row.id !== undefined && row.id !== null ? Number(row.id) : NaN,
            )
            .filter((id) => Number.isFinite(id)),
        );

        const recordsToDelete = Array.from(initialById.entries()).filter(
          ([id]) => !currentIds.has(id),
        );

        if (recordsToDelete.length > 0) {
          await Promise.all(
            recordsToDelete.map(([id, record]) => {
              const blockNumber = lshToStringValue(record?.blockNumber).trim();
              if (!blockNumber) return Promise.resolve();
              return apiDeleteFdApplicationLongTermCrop(id, blockNumber);
            }),
          );
        }

        const payload = longTermRows.map((row) => ({
          ...(row.id ? {id: Number(row.id)} : {}),
          applicationId: Number(application.id),
          cropId: Number(row.cropId),
          blockNumber: row.blockNumber.trim(),
          age: lshToIntOrZero(row.age),
          numberOfTrees: lshToNumberOrZero(row.numberOfTrees),
          area: lshToNumberOrZero(row.area),
          trellies: row.trellies === 'YES',
          spacing: lshToIntOrZero(row.spacing),
          irrigation: row.irrigation.trim(),
          type: row.type.trim(),
          notes: row.notes.trim(),
          projectedYear: lshToIntOrZero(row.projectedYear),
          projectedYield: lshToNumberOrZero(row.projectedYield),
          projectedPrice: lshToNumberOrZero(row.projectedPrice),
          projectedArea: lshToNumberOrZero(row.projectedArea),
          historyYear1: lshToIntOrZero(row.historyYear1),
          historyYield1: lshToNumberOrZero(row.historyYield1),
          historyArea1: lshToNumberOrZero(row.historyArea1),
          historyYear2: lshToIntOrZero(row.historyYear2),
          historyYield2: lshToNumberOrZero(row.historyYield2),
          historyArea2: lshToNumberOrZero(row.historyArea2),
          historyYear3: lshToIntOrZero(row.historyYear3),
          historyYield3: lshToNumberOrZero(row.historyYield3),
          historyArea3: lshToNumberOrZero(row.historyArea3),
        }));

        const savedRecords = await apiCreateOrUpdateFdLongTermCrops(payload);
        const normalizedSavedRecords = (
          Array.isArray(savedRecords) ? savedRecords : []
        ) as ILongTermCropRecord[];

        const cropNameLookup = new Map<string, string>();
        longTermCropOptions.forEach((crop) => {
          cropNameLookup.set(crop.id, crop.name);
        });
        normalizedSavedRecords.forEach((record) => {
          const cropId = record?.crop?.id;
          if (cropId === undefined || cropId === null) return;
          const key = String(cropId);
          if (!cropNameLookup.has(key)) {
            cropNameLookup.set(
              key,
              String(record?.crop?.name || '').trim() || 'Unknown crop',
            );
          }
        });

        const mappedRows = normalizedSavedRecords
          .map((record) => lshMapLongTermRecordToEditableRow(record))
          .sort((left, right) => {
            const cropLeft = (
              cropNameLookup.get(left.cropId) || ''
            ).toLowerCase();
            const cropRight = (
              cropNameLookup.get(right.cropId) || ''
            ).toLowerCase();
            if (cropLeft !== cropRight)
              return cropLeft.localeCompare(cropRight);
            return left.blockNumber
              .toLowerCase()
              .localeCompare(right.blockNumber.toLowerCase());
          });

        setLongTermInitialRecords(normalizedSavedRecords);
        setLongTermRows(mappedRows);
        setLongTermTableErrors({});
        setLongTermTableErrorMessage(undefined);
        return true;
      } catch (error: any) {
        console.error(
          '[ApplicationProductionInfoStep] Failed to save long-term crops',
          error,
        );
        setLongTermTableErrorMessage(
          error?.message ||
            "We couldn't save long-term crops. Please try again.",
        );
        return false;
      } finally {
        setIsSavingLongTermCrops(false);
      }
    }, [
      application?.id,
      handleValidateLongTermRows,
      isLoadingLongTermCrops,
      isSavingLongTermCrops,
      longTermCropOptions,
      longTermInitialRecords,
      longTermRows,
    ]);

  const handleUpdateProductionPlanRowField = useCallback(
    (
      rowId: string,
      field: IEditableProductionPlanField,
      value: string,
    ): void => {
      setProductionPlanRows((current) =>
        current.map((row) =>
          row.rowId === rowId
            ? {
                ...row,
                [field]: value,
              }
            : row,
        ),
      );

      setProductionPlanTableErrors((current) => {
        const rowErrors = current[rowId];
        if (!rowErrors || (!rowErrors[field] && !rowErrors.general))
          return current;

        const next = {...current};
        const nextRowErrors = {...rowErrors};
        delete nextRowErrors[field];
        delete nextRowErrors.general;

        if (Object.keys(nextRowErrors).length === 0) {
          delete next[rowId];
        } else {
          next[rowId] = nextRowErrors;
        }

        return next;
      });

      setProductionPlanTableErrorMessage(undefined);
    },
    [],
  );

  const handleAddProductionPlanCropRow = useCallback(() => {
    if (!selectedProductionPlanCropToAdd) {
      setProductionPlanTableErrorMessage(
        'Choose a crop before adding it to the table.',
      );
      return;
    }

    if (
      productionPlanRows.some(
        (row) => row.cropId === selectedProductionPlanCropToAdd,
      )
    ) {
      setProductionPlanTableErrorMessage(
        'That crop is already captured in the production plan.',
      );
      return;
    }

    setProductionPlanRows((current) => [
      ...current,
      lshCreateEmptyProductionPlanRow(selectedProductionPlanCropToAdd),
    ]);
    setSelectedProductionPlanCropToAdd('');
    setProductionPlanTableErrorMessage(undefined);
  }, [productionPlanRows, selectedProductionPlanCropToAdd]);

  const handleRemoveProductionPlanCropRow = useCallback((cropId: string) => {
    setProductionPlanRows((current) =>
      current.filter((row) => row.cropId !== cropId),
    );

    setProductionPlanTableErrors((current) => {
      const next = {...current};
      delete next[cropId];
      return next;
    });

    setProductionPlanTableErrorMessage(undefined);
  }, []);

  const handleValidateProductionPlanRows = useCallback((): boolean => {
    const nextErrors: IProductionPlanTableErrors = {};

    productionPlanRows.forEach((row) => {
      const rowErrors: IProductionPlanRowErrors = {};

      if (!row.numberOfHectares.trim()) {
        rowErrors.numberOfHectares = 'Enter number of hectares.';
      } else if (!Number.isFinite(Number(row.numberOfHectares))) {
        rowErrors.numberOfHectares = 'Use a numeric hectares value.';
      }

      if (!row.plantingMonth.trim()) {
        rowErrors.plantingMonth = 'Select planting month.';
      }

      if (row.harvestMonth && !row.harvestYear.trim()) {
        rowErrors.harvestYear =
          'Select harvest year when harvest month is set.';
      }

      const optionalNumericFields: Array<
        [IEditableProductionPlanField, string]
      > = [
        ['estimatedYield', row.estimatedYield],
        ['estimatedPrice', row.estimatedPrice],
        ['plantPopulation', row.plantPopulation],
        ['areaUnderIrrigation', row.areaUnderIrrigation],
        ['areaOfDryLand', row.areaOfDryLand],
      ];

      optionalNumericFields.forEach(([field, value]) => {
        if (!value.trim()) return;
        if (!Number.isFinite(Number(value))) {
          rowErrors[field] = 'Use a numeric value.';
        }
      });

      if (row.harvestYear.trim() && !Number.isFinite(Number(row.harvestYear))) {
        rowErrors.harvestYear = 'Use a numeric harvest year.';
      }

      if (Object.keys(rowErrors).length > 0) {
        nextErrors[row.rowId] = rowErrors;
      }
    });

    setProductionPlanTableErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setProductionPlanTableErrorMessage(
        'Please fix the highlighted production plan rows before saving.',
      );
      return false;
    }

    setProductionPlanTableErrorMessage(undefined);
    return true;
  }, [productionPlanRows]);

  const handleSaveProductionPlanStep =
    useCallback(async (): Promise<boolean> => {
      if (
        !application?.id ||
        isSavingProductionPlans ||
        isLoadingProductionPlans
      ) {
        return false;
      }

      if (productionPlanRows.length === 0) {
        setProductionPlanTableErrorMessage(
          'Add at least one production plan crop before continuing.',
        );
        return false;
      }

      if (!handleValidateProductionPlanRows()) {
        return false;
      }

      try {
        setIsSavingProductionPlans(true);

        const initialIds = new Set<number>(
          productionPlanInitialRecords
            .map((record) =>
              record?.id !== undefined && record?.id !== null
                ? Number(record.id)
                : NaN,
            )
            .filter((id) => Number.isFinite(id)),
        );

        const currentIds = new Set<number>(
          productionPlanRows
            .map((row) =>
              row.id !== undefined && row.id !== null ? Number(row.id) : NaN,
            )
            .filter((id) => Number.isFinite(id)),
        );

        const idsToDelete = Array.from(initialIds).filter(
          (id) => !currentIds.has(id),
        );

        if (idsToDelete.length > 0) {
          await Promise.all(
            idsToDelete.map((id) => apiDeleteFdProductionPlan(id)),
          );
        }

        const productionPlansData: ISaveFdProductionPlanDto[] =
          productionPlanRows.map((row) => ({
            ...(row.id ? {id: Number(row.id)} : {}),
            cropId: Number(row.cropId),
            plantingDate: lshToIsoPlantingDate(row.plantingMonth || '1'),
            irrigation: lshToIrrigationValue(String(row.irrigation || '')),
            plantPopulation: lshToNumberOrZero(row.plantPopulation),
            estimatedPrice: lshToNumberOrZero(row.estimatedPrice),
            estimatedYield: lshToNumberOrZero(row.estimatedYield),
            areaOfDryLand: lshToNumberOrZero(row.areaOfDryLand),
            areaUnderIrrigation: lshToNumberOrZero(row.areaUnderIrrigation),
            applicationId: Number(application.id),
            numberOfHectares: lshToNumberOrZero(row.numberOfHectares),
            notes: row.notes || '',
            harvestYear: row.harvestYear ? Number(row.harvestYear) : null,
            harvestMonth: row.harvestMonth ? Number(row.harvestMonth) : null,
          }));

        const savedRecords = await apiUpdateFdProductionPlans(
          productionPlansData,
        );
        const normalizedSavedRecords = (
          Array.isArray(savedRecords) ? savedRecords : []
        ) as IFdProductionPlan[];

        const cropNameLookup = new Map<string, string>();
        productionPlanCropOptions.forEach((crop) => {
          cropNameLookup.set(crop.id, crop.name);
        });
        normalizedSavedRecords.forEach((record) => {
          const cropIdRaw = record?.crop?.id;
          if (cropIdRaw === undefined || cropIdRaw === null) return;
          const key = String(cropIdRaw);
          if (!cropNameLookup.has(key)) {
            cropNameLookup.set(
              key,
              String(record?.crop?.name || '').trim() || 'Unknown crop',
            );
          }
        });

        const mappedRows = normalizedSavedRecords
          .map((record) => lshMapProductionPlanRecordToEditableRow(record))
          .filter((row): row is IEditableProductionPlanRow => Boolean(row))
          .sort((left, right) => {
            const nameLeft = (
              cropNameLookup.get(left.cropId) || ''
            ).toLowerCase();
            const nameRight = (
              cropNameLookup.get(right.cropId) || ''
            ).toLowerCase();
            return nameLeft.localeCompare(nameRight);
          });

        setProductionPlanInitialRecords(normalizedSavedRecords);
        setProductionPlanRows(mappedRows);
        setProductionPlanTableErrors({});
        setProductionPlanTableErrorMessage(undefined);
        setSelectedProductionPlanCropToAdd('');

        setApplication((current) => {
          if (!current) return current;
          return {
            ...current,
            productionPlans: normalizedSavedRecords,
          };
        });

        return true;
      } catch (error: any) {
        console.error(
          '[ApplicationProductionInfoStep] Failed to save production plans',
          error,
        );
        setProductionPlanTableErrorMessage(
          error?.message ||
            "We couldn't save production plans. Please try again.",
        );
        return false;
      } finally {
        setIsSavingProductionPlans(false);
      }
    }, [
      application?.id,
      handleValidateProductionPlanRows,
      isLoadingProductionPlans,
      isSavingProductionPlans,
      productionPlanCropOptions,
      productionPlanInitialRecords,
      productionPlanRows,
      setApplication,
    ]);

  const handleUpdateShortTermHistoryRowField = useCallback(
    (rowId: string, field: IEditableHistoryField, value: string) => {
      setShortTermHistoryRows((current) =>
        current.map((row) =>
          row.rowId === rowId ? {...row, [field]: value} : row,
        ),
      );

      setShortTermHistoryTableErrors((current) => {
        const rowErrors = current[rowId];
        if (!rowErrors || (!rowErrors[field] && !rowErrors.general)) {
          return current;
        }

        const next = {...current};
        const nextRowErrors = {...rowErrors};
        delete nextRowErrors[field];
        delete nextRowErrors.general;

        if (Object.keys(nextRowErrors).length === 0) {
          delete next[rowId];
        } else {
          next[rowId] = nextRowErrors;
        }

        return next;
      });

      setShortTermHistoryTableErrorMessage(undefined);
    },
    [],
  );

  const handleAddShortTermHistoryCropGroup = useCallback(() => {
    if (!selectedShortTermHistoryCropToAdd) {
      setShortTermHistoryTableErrorMessage(
        'Choose a crop before adding it to the table.',
      );
      return;
    }

    if (
      shortTermHistoryRows.some(
        (row) => row.cropId === selectedShortTermHistoryCropToAdd,
      )
    ) {
      setShortTermHistoryTableErrorMessage(
        'That crop is already captured in the table.',
      );
      return;
    }

    const nextRows = requiredShortTermHistoryYears.map((year) =>
      lshCreateEmptyHistoryRow(selectedShortTermHistoryCropToAdd, year),
    );

    setShortTermHistoryRows((current) => [...nextRows, ...current]);
    setSelectedShortTermHistoryCropToAdd('');
    setShortTermHistoryTableErrorMessage(undefined);
  }, [
    requiredShortTermHistoryYears,
    selectedShortTermHistoryCropToAdd,
    shortTermHistoryRows,
  ]);

  const handleRemoveShortTermHistoryCropGroup = useCallback(
    (cropId: string) => {
      setShortTermHistoryRows((current) =>
        current.filter((row) => row.cropId !== cropId),
      );

      setShortTermHistoryTableErrors((current) => {
        const next = {...current};
        Object.keys(next).forEach((rowId) => {
          if (rowId.startsWith(`${cropId}-`)) {
            delete next[rowId];
          }
        });
        return next;
      });

      setShortTermHistoryTableErrorMessage(undefined);
    },
    [],
  );

  const handleValidateShortTermHistoryRows = useCallback((): boolean => {
    const nextErrors: IHistoryTableErrors = {};

    shortTermHistoryRows.forEach((row) => {
      const rowErrors: IHistoryRowErrors = {};

      if (!row.numberOfHectaresFarmed.trim()) {
        rowErrors.numberOfHectaresFarmed = 'Enter hectares farmed.';
      } else if (!Number.isFinite(Number(row.numberOfHectaresFarmed))) {
        rowErrors.numberOfHectaresFarmed = 'Use a numeric hectares value.';
      }

      if (!row.yieldPerHectare.trim()) {
        rowErrors.yieldPerHectare = 'Enter yield per hectare.';
      } else if (!Number.isFinite(Number(row.yieldPerHectare))) {
        rowErrors.yieldPerHectare = 'Use a numeric yield value.';
      }

      if (row.plantMonth && !row.plantYear) {
        rowErrors.plantYear = 'Select planting year when month is set.';
      }

      if (row.harvestMonth && !row.harvestYear) {
        rowErrors.harvestYear = 'Select harvest year when month is set.';
      }

      if (Object.keys(rowErrors).length > 0) {
        nextErrors[row.rowId] = rowErrors;
      }
    });

    const groupedRows = new Map<string, IEditableHistoryRow[]>();
    shortTermHistoryRows.forEach((row) => {
      const existing = groupedRows.get(row.cropId) || [];
      existing.push(row);
      groupedRows.set(row.cropId, existing);
    });

    groupedRows.forEach((cropRows) => {
      const years = new Set(cropRows.map((row) => row.year));
      const fullBlock =
        cropRows.length === requiredShortTermHistoryYears.length &&
        requiredShortTermHistoryYears.every((year) => years.has(year));

      if (!fullBlock) {
        cropRows.forEach((row) => {
          nextErrors[row.rowId] = {
            ...(nextErrors[row.rowId] || {}),
            general: `Each crop must include ${requiredShortTermHistoryYears.join(
              ', ',
            )} seasons.`,
          };
        });
      }
    });

    setShortTermHistoryTableErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setShortTermHistoryTableErrorMessage(
        'Please complete hectares and yield for all seasons in each crop block.',
      );
      return false;
    }

    setShortTermHistoryTableErrorMessage(undefined);
    return true;
  }, [requiredShortTermHistoryYears, shortTermHistoryRows]);

  const handleSaveShortTermHistoryStep =
    useCallback(async (): Promise<boolean> => {
      if (
        !application?.id ||
        isLoadingShortTermHistory ||
        isSavingShortTermHistory
      ) {
        return false;
      }

      if (shortTermHistoryRows.length === 0) {
        setShortTermHistoryTableErrorMessage(
          'Add at least one crop before continuing.',
        );
        return false;
      }

      if (!handleValidateShortTermHistoryRows()) {
        return false;
      }

      const farmId = application?.farm?.id ? Number(application.farm.id) : null;

      try {
        setIsSavingShortTermHistory(true);

        const existingRecordIds = new Set<number>(
          shortTermHistoryInitialRecords
            .map((record) =>
              record?.id !== undefined && record?.id !== null
                ? Number(record.id)
                : NaN,
            )
            .filter((id) => Number.isFinite(id)),
        );

        const currentRecordIds = new Set<number>(
          shortTermHistoryRows
            .map((row) =>
              row.id !== undefined && row.id !== null ? Number(row.id) : NaN,
            )
            .filter((id) => Number.isFinite(id)),
        );

        const idsToDelete = Array.from(existingRecordIds).filter(
          (id) => !currentRecordIds.has(id),
        );

        if (idsToDelete.length > 0) {
          await Promise.all(
            idsToDelete.map((id) => apiDeleteFdProductionHistory(String(id))),
          );
        }

        await Promise.all(
          shortTermHistoryRows.map((row) =>
            apiUpdateFdProductionHistory({
              id:
                row.id !== undefined &&
                row.id !== null &&
                Number.isFinite(Number(row.id))
                  ? Number(row.id)
                  : undefined,
              applicationId: Number(application.id),
              farmId,
              cropId: Number(row.cropId),
              year: Number(row.year),
              numberOfHectaresFarmed: Number(row.numberOfHectaresFarmed),
              yieldPerHectare: Number(row.yieldPerHectare),
              notes: row.notes.trim() || undefined,
              plantYear: lshToOptionalNumberValue(row.plantYear),
              plantMonth: lshToOptionalNumberValue(row.plantMonth),
              harvestYear: lshToOptionalNumberValue(row.harvestYear),
              harvestMonth: lshToOptionalNumberValue(row.harvestMonth),
            }),
          ),
        );

        const refreshedRecords = await apiGetFdProductionHistoryByApplicationId(
          Number(application.id),
        );
        const normalizedRecords = (
          Array.isArray(refreshedRecords) ? refreshedRecords : []
        ) as IFdProductionHistory[];

        const cropNameLookup = new Map<string, string>();
        shortTermHistoryCropOptions.forEach((option) => {
          cropNameLookup.set(option.id, option.name);
        });
        normalizedRecords.forEach((record) => {
          const cropIdRaw = record?.crop?.id;
          if (cropIdRaw === undefined || cropIdRaw === null) return;
          const key = String(cropIdRaw);
          if (!cropNameLookup.has(key)) {
            cropNameLookup.set(
              key,
              String(record?.crop?.name || '').trim() || 'Unknown crop',
            );
          }
        });

        const recordByCropYear = new Map<string, IFdProductionHistory>();
        const recordCropIds = new Set<string>();
        [...normalizedRecords]
          .sort((left, right) => Number(right?.id || 0) - Number(left?.id || 0))
          .forEach((record) => {
            const cropIdRaw = record?.crop?.id;
            const yearValue = Number(record?.year ?? NaN);
            if (cropIdRaw === undefined || cropIdRaw === null) return;
            const cropId = String(cropIdRaw);
            recordCropIds.add(cropId);
            if (!Number.isFinite(yearValue)) return;

            const key = `${cropId}-${yearValue}`;
            if (!recordByCropYear.has(key)) {
              recordByCropYear.set(key, record);
            }
          });

        const sortedCropIds = Array.from(recordCropIds).sort((left, right) => {
          const nameLeft = (cropNameLookup.get(left) || '').toLowerCase();
          const nameRight = (cropNameLookup.get(right) || '').toLowerCase();
          return nameLeft.localeCompare(nameRight);
        });

        const normalizedRows = sortedCropIds.flatMap((cropId) =>
          requiredShortTermHistoryYears.map((year) => {
            const existing = recordByCropYear.get(`${cropId}-${year}`);
            return existing
              ? lshMapProductionHistoryRecordToEditableRow(
                  existing,
                  cropId,
                  year,
                )
              : lshCreateEmptyHistoryRow(cropId, year);
          }),
        );

        setShortTermHistoryCropOptions(
          Array.from(cropNameLookup.entries())
            .map(([id, name]) => ({id, name}))
            .sort((left, right) =>
              left.name.toLowerCase().localeCompare(right.name.toLowerCase()),
            ),
        );
        setShortTermHistoryInitialRecords(normalizedRecords);
        setShortTermHistoryRows(normalizedRows);
        setShortTermHistoryTableErrors({});
        setShortTermHistoryTableErrorMessage(undefined);

        setApplication((current) => {
          if (!current) return current;
          return {
            ...current,
            productionHistory: normalizedRecords,
          };
        });

        return true;
      } catch (error: any) {
        console.error(
          '[ApplicationProductionInfoStep] Failed to save short-term history',
          error,
        );
        setShortTermHistoryTableErrorMessage(
          error?.message ||
            "We couldn't save production history. Please try again.",
        );
        return false;
      } finally {
        setIsSavingShortTermHistory(false);
      }
    }, [
      application?.farm?.id,
      application?.id,
      handleValidateShortTermHistoryRows,
      isLoadingShortTermHistory,
      isSavingShortTermHistory,
      requiredShortTermHistoryYears,
      setApplication,
      shortTermHistoryCropOptions,
      shortTermHistoryInitialRecords,
      shortTermHistoryRows,
    ]);

  const handleContinue = async () => {
    if (activeDynamicStep.key === 'commodity-selection') {
      if (selectedCommodityIds.length === 0) {
        setCommodityError('Select at least one commodity to continue.');
        return;
      }

      if (application?.id && !isSavingCommodities) {
        try {
          setCommodityError(null);
          setIsSavingCommodities(true);

          const savedRows = await apiCreateFdApplicationHistoryCommodities(
            selectedCommodityIds.map((commodityTypeId) => ({
              applicationId: Number(application.id),
              commodityTypeId,
            })),
          );

          const persistedSelectionIds = (
            Array.isArray(savedRows) ? savedRows : []
          )
            .map((row) => Number(row?.commodityType?.id))
            .filter((id) => Number.isFinite(id));

          if (persistedSelectionIds.length > 0) {
            setSelectedCommodityIds(persistedSelectionIds);
          }
        } catch (error) {
          console.error(
            '[ApplicationProductionInfoStep] Failed to save commodity selections',
            error,
          );
          setCommodityError(
            'Could not save commodity selections. Please try again.',
          );
          return;
        } finally {
          setIsSavingCommodities(false);
        }
      }
    }

    if (activeDynamicStep.key === 'livestock') {
      const didSaveLivestock = await handleSaveLivestockStep();
      if (!didSaveLivestock) {
        return;
      }
    }

    if (activeDynamicStep.key === 'long-term-crops') {
      const didSaveLongTermCrops = await handleSaveLongTermCropsStep();
      if (!didSaveLongTermCrops) {
        return;
      }
    }

    if (activeDynamicStep.key === 'production-plan') {
      const didSaveProductionPlan = await handleSaveProductionPlanStep();
      if (!didSaveProductionPlan) {
        return;
      }
    }

    if (activeDynamicStep.key === 'short-term-production-history') {
      const didSaveShortTermHistory = await handleSaveShortTermHistoryStep();
      if (!didSaveShortTermHistory) {
        return;
      }
    }

    setApplication((current) => {
      if (!current) return current;

      return {
        ...current,
        progress: Math.max(Number(current.progress || 0), 5),
        subProgress: Math.max(
          Number(current.subProgress || 0),
          activeDynamicIndex + 1,
        ),
      };
    });

    goToNextDynamicStep();
  };

  const renderCommoditySelection = () => (
    <CommoditySelectionSubStep
      commodityError={commodityError}
      isLoadingCommodities={isLoadingCommodities}
      commodityTypes={commodityTypes}
      selectedCommoditySet={selectedCommoditySet}
      isSavingCommodities={isSavingCommodities}
      onToggleCommodity={handleToggleCommodity}
    />
  );

  const renderLivestock = () => (
    <LivestockSubStep
      livestockError={livestockError}
      isLoadingLivestock={isLoadingLivestock}
      isSavingLivestock={isSavingLivestock}
      livestockForm={livestockForm}
      livestockSelectOptions={livestockSelectOptions}
      livestockTypeOptions={livestockTypeOptions}
      sellOffFrequency={sellOffFrequency}
      sellOffMonth={sellOffMonth}
      onceOffMonths={onceOffMonths}
      customMonths={customMonths}
      customSellOffValues={customSellOffValues}
      livestockRecords={livestockRecords}
      onLivestockFormChange={handleLivestockFormChange}
      onSellOffFrequencyChange={handleSellOffFrequencyChange}
      onSellOffMonthChange={handleSellOffMonthChange}
      onCustomSellOffValueChange={handleCustomSellOffValueChange}
      onAddLivestockRecord={handleAddLivestockRecord}
      onRemoveLivestockRecord={handleRemoveLivestockRecord}
    />
  );

  const renderLongTermCrops = () => (
    <LongTermCropsSubStep
      longTermRows={longTermRows}
      longTermColumns={LONG_TERM_COLUMNS}
      longTermCropOptions={longTermCropOptions}
      longTermVarietyOptionsByCropId={longTermVarietyOptionsByCropId}
      longTermTableErrors={longTermTableErrors}
      longTermTableErrorMessage={longTermTableErrorMessage}
      isLoadingLongTermCrops={isLoadingLongTermCrops}
      isSavingLongTermCrops={isSavingLongTermCrops}
      longTermUploadInputRef={longTermUploadInputRef}
      onAddLongTermRow={handleAddLongTermRow}
      onDuplicateLongTermRow={handleDuplicateLongTermRow}
      onRemoveLongTermRow={handleRemoveLongTermRow}
      onUpdateLongTermRowField={(rowId, field, value) =>
        handleUpdateLongTermRowField(
          rowId,
          field as IEditableLongTermField,
          value,
        )
      }
      onDownloadLongTermTemplate={() => {
        void handleDownloadLongTermTemplate();
      }}
      onOpenLongTermTemplatePicker={handleOpenLongTermTemplatePicker}
      onLongTermTemplateUpload={(event) => {
        void handleLongTermTemplateUpload(event);
      }}
    />
  );

  const renderProductionPlan = () => (
    <ProductionPlanSubStep
      selectedProductionPlanCropToAdd={selectedProductionPlanCropToAdd}
      addableProductionPlanCropOptions={addableProductionPlanCropOptions}
      productionPlanRows={productionPlanRows}
      productionPlanCropNameById={productionPlanCropNameById}
      productionPlanTableErrors={productionPlanTableErrors}
      productionPlanTableErrorMessage={productionPlanTableErrorMessage}
      productionPlanMonthOptions={PRODUCTION_PLAN_MONTH_OPTIONS}
      productionPlanIrrigationOptions={PRODUCTION_PLAN_IRRIGATION_OPTIONS}
      isLoadingProductionPlans={isLoadingProductionPlans}
      isSavingProductionPlans={isSavingProductionPlans}
      hasAttemptedProductionPlanLoad={hasAttemptedProductionPlanLoad}
      onSelectedProductionPlanCropToAddChange={
        setSelectedProductionPlanCropToAdd
      }
      onAddProductionPlanCropRow={handleAddProductionPlanCropRow}
      onUpdateProductionPlanRowField={(rowId, field, value) =>
        handleUpdateProductionPlanRowField(
          rowId,
          field as IEditableProductionPlanField,
          value,
        )
      }
      onRemoveProductionPlanCropRow={handleRemoveProductionPlanCropRow}
    />
  );

  const renderShortTermHistory = () => (
    <ShortTermHistorySubStep
      requiredShortTermHistoryYears={requiredShortTermHistoryYears}
      selectedShortTermHistoryCropToAdd={selectedShortTermHistoryCropToAdd}
      addableShortTermHistoryCropOptions={addableShortTermHistoryCropOptions}
      shortTermHistoryGroupedRows={shortTermHistoryGroupedRows}
      shortTermHistoryTableErrors={shortTermHistoryTableErrors}
      shortTermHistoryTableErrorMessage={shortTermHistoryTableErrorMessage}
      productionHistoryMonthOptions={PRODUCTION_HISTORY_MONTH_OPTIONS}
      productionHistoryYearOptions={PRODUCTION_HISTORY_YEAR_OPTIONS}
      isLoadingShortTermHistory={isLoadingShortTermHistory}
      isSavingShortTermHistory={isSavingShortTermHistory}
      hasAttemptedShortTermHistoryLoad={hasAttemptedShortTermHistoryLoad}
      onSelectedShortTermHistoryCropToAddChange={
        setSelectedShortTermHistoryCropToAdd
      }
      onAddShortTermHistoryCropGroup={handleAddShortTermHistoryCropGroup}
      onUpdateShortTermHistoryRowField={(rowId, field, value) =>
        handleUpdateShortTermHistoryRowField(
          rowId,
          field as IEditableHistoryField,
          value,
        )
      }
      onRemoveShortTermHistoryCropGroup={handleRemoveShortTermHistoryCropGroup}
    />
  );

  const renderSubStepContent = () => {
    if (activeDynamicStep.key === 'commodity-selection') {
      return renderCommoditySelection();
    }

    if (activeDynamicStep.key === 'livestock') {
      return renderLivestock();
    }

    if (activeDynamicStep.key === 'long-term-crops') {
      return renderLongTermCrops();
    }

    if (activeDynamicStep.key === 'production-plan') {
      return renderProductionPlan();
    }

    return renderShortTermHistory();
  };

  const isCommoditySelectionStep =
    activeDynamicStep.key === 'commodity-selection';
  const isLivestockStep = activeDynamicStep.key === 'livestock';
  const isLongTermStep = activeDynamicStep.key === 'long-term-crops';
  const isProductionPlanStep = activeDynamicStep.key === 'production-plan';
  const isShortTermHistoryStep =
    activeDynamicStep.key === 'short-term-production-history';

  return (
    <>
      <ApplicationJourneySubStepProgress
        steps={dynamicSteps.map(({key, title}) => ({key, title}))}
        activeIndex={activeDynamicIndex}
        ariaLabel="Production information sub-steps"
        onStepClick={(index) =>
          goToStep('production-info', {subStepKey: dynamicSteps[index].key})
        }
      />
      <ApplicationJourneyStepLayout
        title={activeDynamicStep.title}
        description={activeDynamicStep.description}
        secondaryAction={{
          label: 'Back',
          onClick: goToPreviousDynamicStep,
        }}
        primaryAction={{
          label: isCommoditySelectionStep
            ? isSavingCommodities
              ? 'Saving...'
              : 'Save & continue'
            : isLivestockStep
            ? isSavingLivestock
              ? 'Saving...'
              : 'Save & continue'
            : isLongTermStep
            ? isSavingLongTermCrops
              ? 'Saving...'
              : 'Save & continue'
            : isProductionPlanStep
            ? isSavingProductionPlans
              ? 'Saving...'
              : 'Save & continue'
            : isShortTermHistoryStep
            ? isSavingShortTermHistory
              ? 'Saving...'
              : 'Save & continue'
            : activeDynamicIndex === dynamicSteps.length - 1
            ? 'Continue to financials'
            : 'Continue',
          onClick: handleContinue,
          disabled: isCommoditySelectionStep
            ? isLoadingCommodities || isSavingCommodities
            : isLivestockStep
            ? isLoadingLivestock || isSavingLivestock
            : isLongTermStep
            ? isLoadingLongTermCrops || isSavingLongTermCrops
            : isProductionPlanStep
            ? isLoadingProductionPlans || isSavingProductionPlans
            : isShortTermHistoryStep
            ? isLoadingShortTermHistory || isSavingShortTermHistory
            : false,
          loading: isCommoditySelectionStep
            ? isSavingCommodities
            : isLivestockStep
            ? isSavingLivestock
            : isLongTermStep
            ? isSavingLongTermCrops
            : isProductionPlanStep
            ? isSavingProductionPlans
            : isShortTermHistoryStep
            ? isSavingShortTermHistory
            : false,
        }}
      >
        {renderSubStepContent()}
      </ApplicationJourneyStepLayout>
    </>
  );
};

export default ApplicationProductionInfoStep;
